// ChatComponent.jsx (React)
import { useEffect, useState, useRef, forwardRef, useImperativeHandle, useCallback, useMemo, useReducer } from 'react';
import MarkdownEditor from './MarkdownEditor';
import ChatTimeline from './ChatTimeline';
import MainStore from './MainStore';
import clsx from 'clsx';
import { HeroIcon } from './HeroIcon';
import { Badge } from './@components/badge';
import { deepEqualIgnoreOrder, isObjectId } from './SharedTransforms';
import { Button } from '@heroui/react';
import { Icon } from '@iconify/react/dist/iconify.js';
import useDataEvent from './CustomHook';
import ConfirmationPopup from './ConfirmationPopup';

const MODE_ICON_MAP = {
  search: 'MagnifyingGlassIcon',
  insights: 'ChartBarIcon',
  slides: 'PresentationChartLineIcon',
  viz: 'ChartPieIcon',
  flow_builder: 'PuzzlePieceIcon',
  summary: 'DocumentTextIcon',
};

const DEFAULT_MODE_ICON = 'Squares2X2Icon';
const COMPLETED_STATUSES = ['done', 'completed', 'success'];
const SKIPPED_STATUSES = ['skipped'];
const PLAN_EVENT_FIELDS =
  'control_update set_field set_parameter parameter_update relationship_update add_primitives remove_primitives new_primitives';
const ADVISOR_ACTIONABLE_TYPES = new Set(['analysis_ready', 'gap_fill_request', 'visualization_ready']);
const MAX_ADVISOR_SUGGESTIONS = 5;
const ADVISOR_DEFAULT_MESSAGE = 'Advisor suggestion';

function mergeExecutionState(sessionState, artifactState) {
  if (!sessionState && !artifactState) {
    return null;
  }
  if (!sessionState) {
    return artifactState;
  }
  if (!artifactState) {
    return sessionState;
  }
  const merged = { ...sessionState };
  const artifactHasPendingSelection = Object.prototype.hasOwnProperty.call(
    artifactState,
    'pendingSelection'
  );
  if (artifactHasPendingSelection) {
    if (artifactState.pendingSelection) {
      merged.pendingSelection = artifactState.pendingSelection;
    } else {
      delete merged.pendingSelection;
    }
  } else if (!merged.pendingSelection && artifactState.pendingSelection) {
    merged.pendingSelection = artifactState.pendingSelection;
  }
  if (artifactState.runner) {
    merged.runner = {
      ...(sessionState?.runner || {}),
      ...artifactState.runner,
    };
  }
  if (artifactState.status) {
    merged.status = artifactState.status;
  }
  if (artifactState.lastRunAt) {
    merged.lastRunAt = artifactState.lastRunAt;
  }
  if (artifactState.primaryPrimitiveId) {
    merged.primaryPrimitiveId = artifactState.primaryPrimitiveId;
  }
  if (artifactState.startedAt) {
    merged.startedAt = artifactState.startedAt;
  }
  const artifactHasAwaitingInputReadyAt = Object.prototype.hasOwnProperty.call(
    artifactState,
    'awaitingInputReadyAt'
  );
  if (artifactHasAwaitingInputReadyAt) {
    if (artifactState.awaitingInputReadyAt) {
      merged.awaitingInputReadyAt = artifactState.awaitingInputReadyAt;
    } else {
      delete merged.awaitingInputReadyAt;
    }
  } else if (artifactState.awaitingInputReadyAt) {
    merged.awaitingInputReadyAt = artifactState.awaitingInputReadyAt;
  }
  const mergedRunnerStatusRaw = merged?.runner?.status ?? merged?.status ?? '';
  const mergedRunnerStatus =
    typeof mergedRunnerStatusRaw === 'string' ? mergedRunnerStatusRaw.toLowerCase() : '';
  if (
    mergedRunnerStatus &&
    [
      'completed',
      'complete',
      'success',
      'done',
      'cancelled',
      'canceled',
      'failed',
      'error',
    ].includes(mergedRunnerStatus)
  ) {
    delete merged.pendingSelection;
    delete merged.awaitingInputReadyAt;
  }
  return merged;
}

function buildAdvisorBadgeData(suggestion) {
  if (!suggestion || !suggestion.id) {
    return null;
  }
  return {
    id: suggestion.id,
    message: suggestion.message || suggestion.label || suggestion.optionId || ADVISOR_DEFAULT_MESSAGE,
    note: suggestion.note ?? null,
    typeLabel: resolveAdvisorTypeLabel(suggestion.type),
    actionable: ADVISOR_ACTIONABLE_TYPES.has(suggestion.type),
  };
}

function isAdvisorBadgeMessage(entry) {
  return Boolean(entry?.metadata?.advisorSuggestionId);
}

function compactGoalPlanBadgeData(entry) {
  const metadata = entry?.metadata ?? {};
  const badgeData = entry?.badgeData ?? {};
  const plan = badgeData?.plan ?? null;
  const goalPlanId = metadata.goalPlanId ?? plan?.id ?? null;
  const goals = Array.isArray(plan?.goals) ? plan.goals : [];
  const goalIds = goals.map((goal) => goal?.id).filter(Boolean);
  const status = badgeData?.status ?? metadata?.goalPlanStatus ?? "pending";
  const createdAt = plan?.createdAt ?? metadata?.goalPlanCreatedAt ?? null;
  const ref = {
    type: "goal_plan_ref",
    goalPlanId,
    status,
    goalIds,
    createdAt,
  };
  return {
    ...entry,
    badgeData: {
      status,
      planRef: ref,
    },
    metadata: {
      ...metadata,
      goalPlanId,
      goalPlanStatus: status,
      goalPlanCreatedAt: createdAt,
      goalPlanGoalIds: goalIds,
    },
  };
}

function resolveGoalPlanIdFromEntry(entry) {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const metadataId = entry?.metadata?.goalPlanId ?? null;
  if (metadataId) {
    return metadataId;
  }
  const badgePlanId = entry?.badgeData?.plan?.id ?? null;
  if (badgePlanId) {
    return badgePlanId;
  }
  const badgeRefId = entry?.badgeData?.planRef?.goalPlanId ?? null;
  if (badgeRefId) {
    return badgeRefId;
  }
  const contextRefId = entry?.context?.goalPlanRef?.goalPlanId ?? null;
  if (contextRefId) {
    return contextRefId;
  }
  const contextPlanId = entry?.context?.goalPlan?.id ?? null;
  if (contextPlanId) {
    return contextPlanId;
  }
  return null;
}

function compactHistoryEntry(entry) {
  if (!entry || typeof entry !== 'object') {
    return entry;
  }
  if (entry.badgeType === 'goal_plan') {
    return compactGoalPlanBadgeData(entry);
  }
  return entry;
}

function filterAdvisorBadgeMessages(entries = []) {
  if (!Array.isArray(entries)) {
    return [];
  }
  return entries
    .filter((entry) => !isAdvisorBadgeMessage(entry))
    .map((entry) => compactHistoryEntry(entry));
}

function serializeAdvisorSuggestionPayload(suggestion) {
  if (!suggestion || !suggestion.id) {
    return null;
  }
  return {
    id: suggestion.id,
    label: suggestion.label ?? suggestion.message ?? suggestion.optionId ?? ADVISOR_DEFAULT_MESSAGE,
    message: suggestion.message ?? null,
    note: suggestion.note ?? null,
    type: suggestion.type ?? null,
    optionId: suggestion.optionId ?? null,
    actionable:
      suggestion.actionable !== undefined
        ? Boolean(suggestion.actionable)
        : ADVISOR_ACTIONABLE_TYPES.has(suggestion.type),
  };
}

function extractActiveAdvisorSuggestionsFromHistory(messages = []) {
  if (!Array.isArray(messages)) {
    return [];
  }
  const latestById = new Map();
  messages.forEach((entry) => {
    if (!entry || entry.badgeType !== 'advisor') {
      return;
    }
    const metadata = entry.metadata ?? {};
    const suggestionId = metadata.advisorSuggestionId;
    if (!suggestionId) {
      return;
    }
    if (metadata.advisorSuggestionStatus === 'handled') {
      latestById.delete(suggestionId);
      return;
    }
    const payload = metadata.advisorSuggestionPayload;
    if (payload && payload.id === suggestionId) {
      latestById.set(suggestionId, payload);
      return;
    }
    const badgeData = entry.badgeData ?? {};
    latestById.set(suggestionId, {
      id: suggestionId,
      label: badgeData.message ?? ADVISOR_DEFAULT_MESSAGE,
      message: badgeData.message ?? null,
      note: badgeData.note ?? null,
      type: metadata.advisorSuggestionType ?? null,
      optionId: null,
      actionable: Boolean(badgeData.actionable),
    });
  });
  return Array.from(latestById.values());
}

function AdvisorSuggestionBadge({ data, suggestion, onAction, displayIndex }) {
  if (!data) {
    return null;
  }
  const actionable = Boolean(data.actionable && suggestion && typeof onAction === 'function');
  const headline = data.message || suggestion?.message || suggestion?.label || ADVISOR_DEFAULT_MESSAGE;
  const typeBadge = data.typeLabel ? (
    <Badge color="purple" className="text-[10px] font-semibold uppercase tracking-wide text-purple-700 dark:text-purple-200">
      {data.typeLabel}
    </Badge>
  ) : null;
  const actionButtons = actionable ? (
    <div className="flex flex-wrap gap-2">
      <Button
        color="primary"
        size="sm"
        className="rounded-full text-xs"
        onPress={() => onAction('accept', suggestion, displayIndex)}
      >
        Accept & run
      </Button>
      <Button
        variant="light"
        size="sm"
        className="rounded-full text-xs"
        onPress={() => onAction('decline', suggestion, displayIndex)}
      >
        Decline
      </Button>
    </div>
  ) : null;
  return (
    <div className="inline-flex w-full max-w-[420px] flex-col gap-2 rounded-2xl border border-slate-200 bg-white/95 px-3 py-2 text-left text-sm text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-300">
      <div className="flex items-start justify-between gap-2 text-[11px] font-semibold uppercase tracking-wide text-purple-600 dark:text-purple-300">
        <div className="flex min-w-0 items-center gap-2">
          <HeroIcon icon="SparklesIcon" className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">{headline}</span>
        </div>
        {typeof displayIndex === 'number' && (
          <span className="rounded-full bg-purple-50 px-2 py-0.5 text-xs font-semibold text-purple-700 dark:bg-purple-500/20 dark:text-purple-200">
            #{displayIndex}
          </span>
        )}
      </div>
      {data.note && (
        <div className="flex items-start gap-1 text-[11px] text-slate-500 dark:text-slate-300">
          <HeroIcon icon="InformationCircleIcon" className="h-3 w-3 flex-shrink-0 text-slate-400 dark:text-slate-300" />
          <span>{data.note}</span>
        </div>
      )}
      {typeof displayIndex === 'number' && (
        <div className="text-[11px] text-slate-500 dark:text-slate-300">
          Reply with <code className="rounded bg-slate-100 px-1 py-0.5 text-[10px] dark:bg-white/10">{`advisor accept ${displayIndex}`}</code> or{' '}
          <code className="rounded bg-slate-100 px-1 py-0.5 text-[10px] dark:bg-white/10">{`advisor decline ${displayIndex}`}</code> to act on this suggestion.
        </div>
      )}
      {(actionButtons || typeBadge) && (
        <div
          className={clsx(
            'mt-2 flex flex-wrap items-center gap-2 pt-1',
            actionButtons ? 'justify-between' : 'justify-end'
          )}
        >
          {actionButtons}
          {typeBadge}
        </div>
      )}
    </div>
  );
}

function GoalPlanCard({ data, onAction, onFocusPlan, planFocused = false }) {
  if (!data?.plan) {
    const headerText =
      data?.status === 'accepted'
        ? 'Plan accepted'
      : data?.status === 'declined'
        ? 'Plan declined'
        : data?.status === 'updated'
          ? 'Plan updated'
          : 'Proposed goal plan';
    return (
      <div className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
        <div className="flex items-start justify-between gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
          <div className="flex min-w-0 items-center gap-2">
            <HeroIcon icon="PauseIcon" className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{headerText}</span>
          </div>
          <button
            type="button"
            title={planFocused ? "Clear artefact focus" : "Focus artefacts to this plan"}
            onClick={() => onFocusPlan?.()}
            className={clsx(
              "inline-flex h-6 w-6 items-center justify-center rounded-full border transition",
              planFocused
                ? "border-blue-200 bg-blue-50 text-blue-600 dark:border-blue-500/60 dark:bg-blue-900/30 dark:text-blue-200"
                : "border-slate-200 bg-white text-slate-400 hover:border-slate-300 hover:text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400",
            )}
            disabled={typeof onFocusPlan !== 'function'}
          >
            <HeroIcon icon="EyeIcon" className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="mt-2 rounded-xl border border-slate-200/80 bg-slate-50/80 px-2 py-2 dark:border-slate-700 dark:bg-slate-800/60">
          <div className="h-3 w-1/3 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
          <div className="mt-2 h-2 w-full animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
          <div className="mt-1 h-2 w-5/6 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
        </div>
      </div>
    );
  }
  const plan = data.plan;
  const status = data.status ?? 'pending';
  const isFinal = status === 'accepted' || status === 'declined';
  const headerText =
    status === 'accepted'
      ? 'Plan accepted'
    : status === 'declined'
      ? 'Plan declined'
      : status === 'updated'
        ? 'Plan updated'
        : 'Proposed goal plan';
  const goals = Array.isArray(plan.goals) ? plan.goals : [];
  const formatGoalParams = (params = {}) => {
    if (!params || typeof params !== 'object') {
      return null;
    }
    const taxonomy = params.taxonomy;
    if (taxonomy) {
      return `Focus: ${taxonomy}`;
    }
    return null;
  };
  const formatGapFill = (entry) => {
    if (!entry?.label) return null;
    const term = entry?.params?.term ?? entry?.params?.focus ?? entry?.params?.dimension ?? null;
    const termSuffix = term ? ` (search: ${term})` : '';
    const statusSuffix =
      entry?.existingStatus === 'in_progress'
        ? ' (in progress)'
        : entry?.existingStatus === 'completed'
          ? ' (already collected)'
          : '';
    return `${entry.label}${termSuffix}${statusSuffix}`;
  };
  const gapFillPreview = Array.isArray(plan.gapFillPreview) ? plan.gapFillPreview : [];
  const gapFillLines = gapFillPreview
    .filter((entry) => entry?.existingStatus !== 'completed' && entry?.existingStatus !== 'in_progress')
    .map(formatGapFill)
    .filter(Boolean);
  const reusedGapFillLines = gapFillPreview
    .filter((entry) => entry?.existingStatus === 'completed' || entry?.existingStatus === 'in_progress')
    .map(formatGapFill)
    .filter(Boolean);
  const dataSourcePreview = Array.isArray(plan.dataSourcePreview) ? plan.dataSourcePreview : [];
  const formatSourceLabel = (value) => {
    if (!value || typeof value !== 'string') return null;
    return value.replace(/_/g, ' ');
  };
  const goalStepPreview = Array.isArray(plan.goalStepPreview) ? plan.goalStepPreview : [];
  const planningDiagnostics = plan?.planningDiagnostics && typeof plan.planningDiagnostics === 'object'
    ? plan.planningDiagnostics
    : {};
  const inputDiagnostics = Array.isArray(planningDiagnostics.inputDiagnostics)
    ? planningDiagnostics.inputDiagnostics
    : [];
  const inputDiagnosticsByStepId = new Map(
    inputDiagnostics
      .filter((entry) => entry?.stepId)
      .map((entry) => [String(entry.stepId), entry])
  );
  const reusedGoalInputStepIds = new Set(
    goals.flatMap((goal) =>
      Object.values(goal?.reuseStepIds ?? {}).filter(Boolean).map((id) => String(id))
    )
  );
  const normalizedPreviewSteps = goalStepPreview
    .map((step, idx) => {
      const stepId = step?.stepId ?? step?.id ?? null;
      if (!stepId) {
        return null;
      }
      const diagnosticsForStep = inputDiagnosticsByStepId.get(String(stepId)) ?? null;
      const inputStepIds = Array.isArray(step?.inputStepIds)
        ? step.inputStepIds
        : Array.isArray(diagnosticsForStep?.inputStepIds)
          ? diagnosticsForStep.inputStepIds
          : [];
      const reusedInputStepIds = inputStepIds
        .map((id) => String(id))
        .filter((id) => reusedGoalInputStepIds.has(id));
      return {
        id: stepId,
        stepId,
        name: step?.name ?? `Step ${idx + 1}`,
        status: 'pending',
        order: Number.isFinite(step?.order) ? step.order : idx,
        metadata: {
          origin: 'goal',
          goalId: step?.goalId ?? null,
          goalLabel: step?.goalLabel ?? null,
          optionId: step?.optionId ?? null,
          templateId: step?.templateId ?? null,
          requestedByStepId: step?.requestedByStepId ?? null,
          kind: step?.kind ?? null,
          reuseStatus: step?.reuseStatus ?? null,
          filterSetSize: Number.isFinite(step?.filterSetSize) ? step.filterSetSize : null,
          filterScope: step?.filterScope ?? null,
          reusedGapfillStepIds: Array.isArray(step?.reusedGapfillStepIds) ? step.reusedGapfillStepIds : [],
          reusedInputStepIds,
        },
        executionState: null,
      };
    })
    .filter(Boolean);
  const hasGapfillStepsInPreview = normalizedPreviewSteps.some(
    (step) => step?.metadata?.kind === 'gap_fill'
  );
  const orderedPreviewSteps = buildSubplanTreeSteps(normalizedPreviewSteps);
  const previewTree = buildTreeNodesFromOrderedSteps(orderedPreviewSteps);
  const normalizePlanLabel = (value) =>
    String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  const goalLabelsNormalized = goals
    .map((goal) => normalizePlanLabel(goal?.label))
    .filter(Boolean);
  const topLevelStepLabelsNormalized = previewTree
    .map((node) => normalizePlanLabel(node?.step?.name))
    .filter(Boolean);
  const showGoalCards =
    goals.length > 0 &&
    (
      goalLabelsNormalized.length !== topLevelStepLabelsNormalized.length ||
      goalLabelsNormalized.some((label, idx) => label !== topLevelStepLabelsNormalized[idx])
    );
  const previewStateById = new Map();
  normalizedPreviewSteps.forEach((step, idx) => {
    const stepId = resolvePlanStepId(step);
    const stepKey = stepId ?? idx;
    const state = derivePlanStepStatus(step, null, null);
    previewStateById.set(stepKey, state);
    if (stepId) {
      previewStateById.set(stepId, state);
    }
  });
  return (
    <div className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
      <div className="flex items-start justify-between gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
        <div className="flex min-w-0 items-center gap-2">
          <HeroIcon icon="PauseIcon" className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">{headerText}</span>
        </div>
        <button
          type="button"
          title={planFocused ? "Clear artefact focus" : "Focus artefacts to this plan"}
          onClick={() => onFocusPlan?.()}
          className={clsx(
            "inline-flex h-6 w-6 items-center justify-center rounded-full border transition",
            planFocused
              ? "border-blue-200 bg-blue-50 text-blue-600 dark:border-blue-500/60 dark:bg-blue-900/30 dark:text-blue-200"
              : "border-slate-200 bg-white text-slate-400 hover:border-slate-300 hover:text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400",
          )}
          disabled={typeof onFocusPlan !== 'function'}
        >
          <HeroIcon icon="EyeIcon" className="h-3.5 w-3.5" />
        </button>
      </div>
      {showGoalCards && (
        <div className="mt-2 flex flex-col gap-1 text-[12px] text-slate-700 dark:text-slate-200">
          {goals.map((goal, idx) => {
            const paramSummary = formatGoalParams(goal.params);
            return (
              <div
                key={goal?.id ?? idx}
                className="flex flex-col gap-1 rounded-xl border border-slate-200/80 bg-slate-50/80 px-2 py-1 dark:border-slate-700 dark:bg-slate-800/60"
              >
                <div className="flex items-start gap-2">
                  <div className="flex flex-col">
                    <span className="font-semibold">{goal.label ?? `Goal ${idx + 1}`}</span>
                    {paramSummary && (
                      <span className="text-[11px] text-slate-500 dark:text-slate-400">{paramSummary}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {previewTree.length > 0 && (
        <div className="mt-2 rounded-xl border border-slate-200/80 bg-slate-50/80 px-2 py-1 dark:border-slate-700 dark:bg-slate-800/60">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Proposed steps
          </div>
          <div className="flex flex-col gap-1 text-[12px] text-slate-600 dark:text-slate-300">
            {previewTree.map((node, idx) => (
              <SubplanTreeNode
                key={node.step?.id ?? idx}
                node={node}
                depth={0}
                isLastSibling={idx === previewTree.length - 1}
                showSiblingConnector={idx < previewTree.length - 1}
                stateById={previewStateById}
                mainstore={null}
                activeStepId={null}
                handlePlanStepActivate={null}
                handlePlanStepDelete={null}
                readOnly
              />
            ))}
          </div>
        </div>
      )}
      {!isFinal && gapFillLines.length > 0 && !hasGapfillStepsInPreview && (
        <div className="mt-2 rounded-xl border border-slate-200/80 bg-slate-50/80 px-2 py-1 text-[11px] text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200">
          <div className="font-semibold uppercase tracking-wide text-[10px] text-slate-500 dark:text-slate-400">
            Data collection
          </div>
          <ul className="list-disc space-y-0.5 pl-4">
            {gapFillLines.map((line, idx) => (
              <li key={`${line}-${idx}`}>{line}</li>
            ))}
          </ul>
        </div>
      )}
      {!isFinal && reusedGapFillLines.length > 0 && !hasGapfillStepsInPreview && (
        <div className="mt-2 rounded-xl border border-emerald-200/80 bg-emerald-50/70 px-2 py-1 text-[11px] text-emerald-800 dark:border-emerald-700/60 dark:bg-emerald-900/30 dark:text-emerald-100">
          <div className="font-semibold uppercase tracking-wide text-[10px] text-emerald-700 dark:text-emerald-200">
            Reused data collection
          </div>
          <ul className="list-disc space-y-0.5 pl-4">
            {reusedGapFillLines.map((line, idx) => (
              <li key={`reused-${line}-${idx}`}>{line}</li>
            ))}
          </ul>
        </div>
      )}
      {!isFinal && dataSourcePreview.length > 0 && (
        <div className="mt-2 rounded-xl border border-slate-200/80 bg-slate-50/80 px-2 py-1 text-[11px] text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200">
          <div className="font-semibold uppercase tracking-wide text-[10px] text-slate-500 dark:text-slate-400">
            Data sources
          </div>
          <div className="mt-1 space-y-2">
            {dataSourcePreview.map((entry, idx) => {
              const selected = Array.isArray(entry?.selectedSources) ? entry.selectedSources : [];
              const missing = Array.isArray(entry?.missingSources) ? entry.missingSources : [];
              const deps = Array.isArray(entry?.dependencies) ? entry.dependencies : [];
              const missingDeps = deps.filter((dep) => dep?.satisfied !== true);
              const readyDeps = deps.filter((dep) => dep?.satisfied === true);
              const missingLabels = Array.from(
                new Set(
                  missingDeps
                    .map((dep) => formatSourceLabel(dep?.sourceKey) ?? dep?.getter ?? dep?.id ?? null)
                    .filter(Boolean)
                )
              );
              return (
                <div
                  key={`${entry?.goalId ?? 'goal'}-${entry?.optionId ?? idx}`}
                  className="rounded-lg border border-slate-200/80 bg-white/70 px-2 py-1 dark:border-slate-700 dark:bg-slate-900/40"
                >
                  <div className="text-[10px] text-slate-500 dark:text-slate-400">
                    Selected: {selected.length ? selected.map((source) => formatSourceLabel(source)).filter(Boolean).join(', ') : 'default source set'}
                  </div>
                  {deps.length > 0 && (
                    <div className="text-[10px] text-slate-500 dark:text-slate-400">
                      Coverage: {readyDeps.length}/{deps.length} dependencies ready
                    </div>
                  )}
                  {(missingLabels.length > 0 || missing.length > 0) && (
                    <div className="text-[10px] text-amber-700 dark:text-amber-300">
                      Missing: {missingLabels.length ? missingLabels.join(', ') : missing.map((source) => formatSourceLabel(source)).filter(Boolean).join(', ')}
                    </div>
                  )}
                  {missingDeps.length > 0 && (
                    <ul className="mt-1 list-disc pl-4 text-[10px] text-slate-600 dark:text-slate-300">
                      {missingDeps.map((dep, depIdx) => (
                        <li key={`${dep?.id ?? dep?.getter ?? depIdx}`}>
                          {(formatSourceLabel(dep?.sourceKey) ?? dep?.getter ?? dep?.id ?? 'dependency')}: {dep?.note ?? 'missing'}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {!isFinal && (
        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            color="primary"
            size="sm"
            className="rounded-full text-xs"
            onPress={() => onAction?.('accept', 'all')}
          >
            Run all goals
          </Button>
          <Button
            variant="light"
            size="sm"
            className="rounded-full text-xs"
            onPress={() => onAction?.('decline', 'all')}
          >
            Decline plan
          </Button>
        </div>
      )}
    </div>
  );
}

function resolveStepOrigin(step) {
  if (!step) {
    return 'core';
  }
  if (step.metadata?.origin) {
    return step.metadata.origin;
  }
  if (step.metadata?.kind === 'gap_fill') {
    return 'gap_fill';
  }
  if (typeof step.id === 'string') {
    if (step.id.startsWith('gapfill_')) {
      return 'gap_fill';
    }
    if (step.id.startsWith('advisor_')) {
      return 'advisor';
    }
  }
  return 'core';
}

function resolvePlanStepId(step) {
  if (!step) {
    return null;
  }
  return step.id ?? step.stepId ?? step.primitiveId ?? null;
}

function SubplanTreeNode({
  node,
  depth,
  isLastSibling,
  showSiblingConnector,
  stateById,
  mainstore,
  activeStepId,
  actionFeedback,
  handlePlanStepActivate,
  handlePlanStepDelete,
  readOnly = false,
}) {
  const step = node.step;
  const stepId = resolvePlanStepId(step) ?? '';
  const stepPrimitiveId = step?.primitiveId ?? null;
  const stateLookupKey = stepId || stepPrimitiveId || '';
  const state = stateById.get(stateLookupKey) ?? derivePlanStepStatus(step, mainstore, activeStepId);
  const durationLabel = resolveStepDurationLabel(step, state.state);
  const { showFocus, showRetry, showSkip, showDelete } = resolvePlanStepActionVisibility(step, state);
  const normalize = (value) => (value === null || value === undefined ? null : String(value));
  const normalizedActiveStepId = normalize(activeStepId);
  const normalizedStepId = normalize(stepId);
  const normalizedStepPrimitiveId = normalize(stepPrimitiveId);
  const normalizedFeedbackStepId = normalize(actionFeedback?.stepId);
  const normalizedFeedbackStepPrimitiveId = normalize(actionFeedback?.stepPrimitiveId);
  const hasActivationFeedback = Boolean(
    actionFeedback &&
      (normalizedFeedbackStepId === normalizedStepId ||
        normalizedFeedbackStepId === normalizedStepPrimitiveId ||
        normalizedFeedbackStepPrimitiveId === normalizedStepId ||
        normalizedFeedbackStepPrimitiveId === normalizedStepPrimitiveId)
  );
  const isRetryFeedback = actionFeedback?.mode === 'retry';
  const isSkipFeedback = actionFeedback?.mode === 'skip';
  const isActiveStep = Boolean(
    normalizedActiveStepId &&
      (normalizedActiveStepId === normalizedStepId ||
        normalizedActiveStepId === normalizedStepPrimitiveId)
  );
  const isUpNext =
    isActiveStep &&
    !(state.state === 'running' && state.label === 'Awaiting') &&
    state.state !== 'running' &&
    state.state !== 'blocked' &&
    state.state !== 'queued';
  const statusIcon =
    isUpNext ? (
      <HeroIcon icon="PauseIcon" className="h-3.5 w-3.5 text-amber-500" />
    ) : state.state === 'running' && state.label === 'Awaiting' ? (
      <HeroIcon icon="PauseIcon" className="h-3.5 w-3.5 text-amber-500" />
    ) : state.state === 'running' ? (
      <HeroIcon icon="ArrowPathIcon" className="h-3.5 w-3.5 animate-spin text-blue-500" />
    ) : state.state === 'done' ? (
      <HeroIcon icon="CheckCircleIcon" className="h-3.5 w-3.5 text-emerald-500" />
    ) : state.state === 'active' ? (
      <HeroIcon icon="BoltIcon" className="h-3.5 w-3.5 text-amber-500" />
    ) : state.state === 'blocked' ? (
      <HeroIcon icon="ExclamationTriangleIcon" className="h-3.5 w-3.5 text-rose-500" />
    ) : state.state === 'queued' ? (
      <HeroIcon icon="ClockIcon" className="h-3.5 w-3.5 text-slate-400" />
    ) : (
      <HeroIcon icon="MinusCircleIcon" className="h-3.5 w-3.5 text-slate-300" />
    );
  const indent = depth * 20;
  const spineLeft = indent - 10;
  const connectorWidth = 20;
  const rootConnectorLeft = 10;
  return (
    <div className="relative">
      <div
        className="relative flex items-center gap-2 py-1"
        style={{ paddingLeft: `${indent}px` }}
      >
        {depth === 0 && showSiblingConnector && (
          <span
            className="absolute w-px bg-slate-200 dark:bg-slate-700"
            style={{ left: `${rootConnectorLeft}px`, top: '-10px', bottom: '-10px' }}
          />
        )}
        {depth > 0 && (
          <>
            <span
              className="absolute w-px bg-slate-200 dark:bg-slate-700"
              style={{
                left: `${spineLeft}px`,
                top: '-10px',
                bottom: isLastSibling ? '50%' : '-10px',
              }}
            />
            <span
              className="absolute top-1/2 h-px bg-slate-200 dark:bg-slate-700"
              style={{ left: `${spineLeft}px`, width: `${connectorWidth}px` }}
            />
          </>
        )}
        <span className="relative z-10 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white dark:bg-slate-900">
          {statusIcon}
        </span>
        <span className="text-slate-700 dark:text-slate-200">{step.name ?? 'Step'}</span>
        {Number.isFinite(step?.metadata?.filterSetSize) && step.metadata.filterSetSize > 0 && (
          <span className="inline-flex items-center rounded-full border border-cyan-200 bg-cyan-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-cyan-700 dark:border-cyan-700/60 dark:bg-cyan-900/30 dark:text-cyan-100">
            Filter {step.metadata.filterSetSize}
          </span>
        )}
        {step?.metadata?.kind === 'gap_fill' && (
          <span
            className={clsx(
              'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              step?.metadata?.reuseStatus === 'reused'
                ? 'border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-700/60 dark:bg-emerald-900/30 dark:text-emerald-100'
                : 'border border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-700/60 dark:bg-indigo-900/30 dark:text-indigo-100'
            )}
          >
            {step?.metadata?.reuseStatus === 'reused' ? 'Reused' : 'Gapfill'}
          </span>
        )}
        {Array.isArray(step?.metadata?.reusedInputStepIds) && step.metadata.reusedInputStepIds.length > 0 && (
          <span
            className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:border-emerald-700/60 dark:bg-emerald-900/30 dark:text-emerald-100"
            title={step.metadata.reusedInputStepIds.join(', ')}
          >
            Reuses {step.metadata.reusedInputStepIds.length} input{step.metadata.reusedInputStepIds.length === 1 ? '' : 's'}
          </span>
        )}
        {isUpNext && (
          <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:border-amber-500/50 dark:bg-amber-900/40 dark:text-amber-100">
            Up next
          </span>
        )}
        {hasActivationFeedback && (
          <span
            className={clsx(
              "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
              isSkipFeedback
                ? "border border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-500/50 dark:bg-slate-800 dark:text-slate-100"
                : isRetryFeedback
                ? "border border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/50 dark:bg-blue-900/40 dark:text-blue-100"
                : "border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/50 dark:bg-emerald-900/40 dark:text-emerald-100"
            )}
          >
            {isSkipFeedback ? 'Skipped' : isRetryFeedback ? 'Retry queued' : 'Set current'}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {durationLabel && (
            <span className="mr-1 inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0 text-[10px] text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              {durationLabel.label}
              <HeroIcon
                icon="ClockIcon"
                className={clsx("h-3 w-3", durationLabel.isRunning ? "animate-pulse" : "")}
              />
            </span>
          )}
          {!readOnly && showFocus && (
            <button
              type="button"
              aria-label="Set current step"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:text-slate-500 dark:hover:text-slate-200"
              onClick={() => handlePlanStepActivate?.(step, null)}
            >
              <HeroIcon icon="BoltIcon" className="h-3.5 w-3.5" />
            </button>
          )}
          {!readOnly && showRetry && (
            <button
              type="button"
              aria-label="Retry step"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:text-slate-500 dark:hover:text-slate-200"
              onClick={() => handlePlanStepActivate?.(step, 'retry')}
            >
              <HeroIcon icon="ArrowPathIcon" className="h-3.5 w-3.5" />
            </button>
          )}
          {!readOnly && showSkip && (
            <button
              type="button"
              aria-label="Skip step"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:text-slate-500 dark:hover:text-slate-200"
              onClick={() => handlePlanStepActivate?.(step, 'skip')}
            >
              <HeroIcon icon="ForwardIcon" className="h-3.5 w-3.5" />
            </button>
          )}
          {!readOnly && showDelete && (
            <button
              type="button"
              aria-label="Delete step"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-300 transition hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-900/30 dark:text-slate-500 dark:hover:text-rose-200"
              onClick={() => handlePlanStepDelete?.(step)}
            >
              <HeroIcon icon="TrashIcon" className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      {node.children?.length > 0 && (
        <div className="relative ml-0">
          {showSiblingConnector && (
            <span
              className="absolute w-px bg-slate-200 dark:bg-slate-700"
              style={{
                left: depth === 0 ? `${rootConnectorLeft}px` : `${spineLeft}px`,
                top: 0,
                bottom: 0,
              }}
            />
          )}
          {node.children.map((child, idx) => (
            <SubplanTreeNode
              key={child.step?.id ?? child.step?.primitiveId ?? idx}
              node={child}
              depth={depth + 1}
              isLastSibling={idx === node.children.length - 1}
              showSiblingConnector={idx < node.children.length - 1}
              stateById={stateById}
              mainstore={mainstore}
              activeStepId={activeStepId}
              actionFeedback={actionFeedback}
              handlePlanStepActivate={handlePlanStepActivate}
              handlePlanStepDelete={handlePlanStepDelete}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function buildSubplanTreeSteps(steps = []) {
  const nodes = new Map();
  const orderLookup = new Map();
  const pipelineKeyToId = new Map();
  steps.forEach((step, idx) => {
    const stepId = resolvePlanStepId(step);
    if (!stepId) {
      return;
    }
    nodes.set(stepId, { step, id: stepId, children: [], parentId: null, idx });
    orderLookup.set(stepId, Number.isFinite(step.order) ? step.order : idx);
    const pipelineKey =
      step?.metadata?.pipeline?.key ??
      step?.executionState?.pipeline?.key ??
      null;
    if (pipelineKey) {
      pipelineKeyToId.set(pipelineKey, stepId);
    }
  });
  const incoming = new Map();
  const outgoing = new Map();
  const addEdge = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) {
      return;
    }
    if (!nodes.has(fromId) || !nodes.has(toId)) {
      return;
    }
    if (!incoming.has(toId)) {
      incoming.set(toId, new Set());
    }
    if (!outgoing.has(fromId)) {
      outgoing.set(fromId, new Set());
    }
    incoming.get(toId).add(fromId);
    outgoing.get(fromId).add(toId);
  };

  nodes.forEach((node) => {
    const step = node.step;
    const metadata = step?.metadata ?? {};
    const execution = step?.executionState ?? {};
    const requestedBy =
      metadata.requestedByStepId ??
      metadata.parentStepId ??
      metadata.sourceStepId ??
      execution.requestedByStepId ??
      execution.parentStepId ??
      execution.sourceStepId ??
      execution.runner?.context?.requestedByStepId ??
      execution.runner?.context?.parentStepId ??
      execution.runner?.context?.sourceStepId ??
      null;
    if (requestedBy) {
      const isGapFill =
        metadata.kind === 'gap_fill' ||
        (typeof step?.id === 'string' && step.id.startsWith('gapfill_'));
      const isFilterTemplate =
        metadata.templateId === 'filter_company_set' ||
        step?.templateId === 'filter_company_set';
      if (isGapFill || isFilterTemplate) {
        addEdge(node.id, requestedBy);
      } else {
        addEdge(requestedBy, node.id);
      }
    }
    const pipeline = metadata.pipeline ?? execution.pipeline ?? null;
    const upstreamKeys = Array.isArray(pipeline?.upstreamKeys) ? pipeline.upstreamKeys : [];
    upstreamKeys.forEach((key) => {
      const upstreamId = pipelineKeyToId.get(key);
      if (upstreamId) {
        addEdge(upstreamId, node.id);
      }
    });
  });

  const sortByOrder = (a, b) => (orderLookup.get(a) ?? 0) - (orderLookup.get(b) ?? 0);
  const sortByNodeOrder = (a, b) => (orderLookup.get(a.id) ?? 0) - (orderLookup.get(b.id) ?? 0);

  const requestedByMap = new Map();
  nodes.forEach((node) => {
    const metadata = node.step?.metadata ?? {};
    const execution = node.step?.executionState ?? {};
    const requestedBy =
      metadata.requestedByStepId ??
      execution.requestedByStepId ??
      null;
    if (requestedBy && nodes.has(requestedBy)) {
      if (!requestedByMap.has(requestedBy)) {
        requestedByMap.set(requestedBy, new Set());
      }
      requestedByMap.get(requestedBy).add(node.id);
    }
  });

  const ordered = [];
  const seen = new Set();
  const walkUpWithinGroup = (nodeId, depth, allowed) => {
    if (!nodeId || seen.has(nodeId)) {
      return;
    }
    seen.add(nodeId);
    const node = nodes.get(nodeId);
    if (!node) {
      return;
    }
    ordered.push({ step: node.step, depth });
    const parents = Array.from(incoming.get(nodeId) ?? [])
      .filter((id) => allowed.has(id))
      .map((id) => nodes.get(id))
      .filter(Boolean)
      .sort(sortByNodeOrder);
    parents.forEach((parent) => walkUpWithinGroup(parent.id, depth + 1, allowed));
  };

  if (requestedByMap.size > 0) {
    const parentIds = Array.from(requestedByMap.keys()).sort(sortByOrder);
    parentIds.forEach((parentId) => {
      const parentNode = nodes.get(parentId);
      if (!parentNode) {
        return;
      }
      if (!seen.has(parentId)) {
        seen.add(parentId);
        ordered.push({ step: parentNode.step, depth: 0 });
      }
      const group = requestedByMap.get(parentId) ?? new Set();
      const groupLeaves = Array.from(group).filter(
        (nodeId) => (outgoing.get(nodeId)?.size ?? 0) === 0 || !Array.from(outgoing.get(nodeId) ?? []).some((id) => group.has(id)),
      );
      groupLeaves.sort(sortByOrder);
      groupLeaves.forEach((leafId) => walkUpWithinGroup(leafId, 1, group));
    });
  }

  nodes.forEach((node) => {
    if (seen.has(node.id)) {
      return;
    }
    ordered.push({ step: node.step, depth: 0 });
  });

  return ordered;
}

function buildTreeNodesFromOrderedSteps(orderedSteps = []) {
  const tree = [];
  const stack = [];
  orderedSteps.forEach(({ step, depth }) => {
    const node = { step, depth, children: [] };
    while (stack.length > depth) {
      stack.pop();
    }
    if (stack.length === 0) {
      tree.push(node);
    } else {
      stack[stack.length - 1].children.push(node);
    }
    stack[depth] = node;
  });
  return tree;
}

function resolveSubplanKey(step) {
  if (!step) {
    return 'core';
  }
  const normalizeQueueGroupKey = (queueGroupId, goalPlanId = null) => {
    const raw = typeof queueGroupId === 'string' ? queueGroupId.trim() : '';
    if (!raw) {
      return null;
    }
    // Already namespaced (goalplan_xxx::goal_...) should be used as-is.
    if (raw.includes('::')) {
      return `goalq_${raw}`;
    }
    if (goalPlanId) {
      return `goalq_${goalPlanId}::${raw}`;
    }
    return `goalq_${raw}`;
  };
  const goalPlanId = step?.metadata?.goalPlanId ?? null;
  const queueGroupId = step?.metadata?.queueGroupId;
  const goalLabel = step?.metadata?.goalLabel ?? null;
  if (queueGroupId) {
    const normalized = normalizeQueueGroupKey(queueGroupId, goalPlanId);
    if (normalized) {
      return normalized;
    }
    // Legacy fallback: queue group ids can collide across goal plans (e.g., goal_g1_1).
    // Split by goal label when no goalPlanId is available on step metadata.
    if (goalLabel) {
      return `goalq_${queueGroupId}::${String(goalLabel).trim().toLowerCase()}`;
    }
    return `goalq_${queueGroupId}`;
  }
  const goalId = step?.metadata?.goalId;
  if (goalId) {
    if (goalPlanId) {
      return `goal_${goalPlanId}::${goalId}`;
    }
    return `goal_${goalId}`;
  }
  const origin = resolveStepOrigin(step);
  if (origin === 'goal') {
    const goalKey = step?.metadata?.queueGroupId || step?.metadata?.goalId || step?.metadata?.goalLabel;
    if (!goalKey) {
      return 'goal';
    }
    if (step?.metadata?.queueGroupId) {
      if (goalPlanId) {
        return `goalq_${goalPlanId}::${goalKey}`;
      }
      return `goalq_${goalKey}`;
    }
    if (goalPlanId) {
      return `goal_${goalPlanId}::${goalKey}`;
    }
    return `goal_${goalKey}`;
  }
  if (origin === 'advisor' || origin === 'gap_fill') {
    const suggestionKey = step?.metadata?.suggestionId || step?.metadata?.sourceStepId || step?.metadata?.optionId;
    return suggestionKey ? `advisor_${suggestionKey}` : 'advisor';
  }
  return 'core';
}

function resolveSubplanLabel(step, labelMaps) {
  const goalId = step?.metadata?.goalId;
  if (goalId) {
    return step?.metadata?.goalLabel || labelMaps?.goalLabel || 'Goal steps';
  }
  const origin = resolveStepOrigin(step);
  if (origin === 'goal') {
    return step?.metadata?.goalLabel || labelMaps?.goalLabel || 'Goal steps';
  }
  if (origin === 'advisor' || origin === 'gap_fill') {
    return labelMaps?.advisorLabel || 'Insight request';
  }
  return 'Core steps';
}

function resolveSubplanInsertionIndex(subplanKey, messageIndexMap, fallbackIndex = -1) {
  if (!subplanKey) {
    return fallbackIndex;
  }
  const idx = messageIndexMap.get(subplanKey);
  if (typeof idx === 'number') {
    return idx;
  }
  return fallbackIndex;
}

function derivePlanStepStatus(step, mainstore, activeStepId) {
  const status = (step?.runtime?.status ?? '').toLowerCase();
  const brightdataInProgress = hasBrightdataProcessing(mainstore, step);
  const runnerStatusRaw = step?.executionState?.runner?.status ?? '';
  const runnerStatus =
    typeof runnerStatusRaw === 'string' ? runnerStatusRaw.toLowerCase() : runnerStatusRaw;
  const executionStatusRaw = step?.executionState?.status ?? '';
  const executionStatus =
    typeof executionStatusRaw === 'string' ? executionStatusRaw.toLowerCase() : executionStatusRaw;
  const exec = step?.executionState ?? {};
  const runner = exec?.runner ?? {};
  const hasStarted = Boolean(
    exec.startedAt || exec.started || runner.startedAt || runner.started
  );
  const hasExecutionSignal =
    Boolean(runnerStatus || executionStatus) ||
    hasStarted;
  const runnerActive =
    runnerStatus && ['running', 'pending', 'starting', 'launching'].includes(runnerStatus);
  const executionActive =
    executionStatus && ['running', 'pending', 'starting', 'launching'].includes(executionStatus);
  const runnerQueued = runnerStatus === 'queued';
  const executionQueued = executionStatus === 'queued';
  const runnerWaitingUser = runnerStatus === 'waiting_user';
  const awaitingRunner = runnerActive || executionActive;
  const awaitingApproval =
    step?.executionState?.awaitingApproval === true &&
    step?.executionState?.userApproved !== true;
  const awaitingInput =
    awaitingApproval ||
    Boolean(step?.executionState?.pendingSelection) ||
    (step?.selection === 'ask' && !step?.selectedActionId);
  const awaitingInputReady = Boolean(step?.executionState?.awaitingInputReadyAt);
  const stepId = resolvePlanStepId(step);
  const stepPrimitiveId = step?.primitiveId ?? null;
  const normalize = (value) => (value === null || value === undefined ? null : String(value));
  const normalizedActive = normalize(activeStepId);
  const normalizedStepId = normalize(stepId);
  const normalizedStepPrimitiveId = normalize(stepPrimitiveId);
  const isActiveStep = Boolean(
    normalizedActive &&
      (normalizedActive === normalizedStepId || normalizedActive === normalizedStepPrimitiveId)
  );
  const isCompleted =
    COMPLETED_STATUSES.includes(status) ||
    ['completed', 'complete', 'success'].includes(runnerStatus) ||
    ['completed', 'complete', 'success'].includes(executionStatus);
  const isSkipped =
    SKIPPED_STATUSES.includes(status) ||
    ['cancelled', 'canceled'].includes(runnerStatus) ||
    ['cancelled', 'canceled'].includes(executionStatus);
  const isBlocked = status === 'blocked';
  const isRunning =
    runnerActive ||
    executionActive ||
    (!runnerStatus && !executionStatus && status === 'in_progress' && hasExecutionSignal && hasStarted) ||
    awaitingRunner;
  if (isCompleted) {
    return { state: 'done', label: 'Done' };
  }
  if (isSkipped) {
    return { state: 'skipped', label: 'Skipped' };
  }
  if (isBlocked) {
    return { state: 'blocked', label: 'Blocked' };
  }
  if (isActiveStep) {
    if (runnerActive || executionActive || awaitingRunner) {
      return { state: 'running', label: 'Running' };
    }
    if ((runnerQueued || executionQueued) && hasStarted) {
      return { state: 'running', label: 'Running' };
    }
    if (runnerWaitingUser || awaitingInput || awaitingInputReady) {
      return { state: 'active', label: 'Awaiting' };
    }
    if (runnerQueued || executionQueued) {
      return { state: 'queued', label: 'Queued' };
    }
    if (brightdataInProgress && !isCompleted && !isSkipped) {
      return { state: 'running', label: 'Running' };
    }
    if (status === 'in_progress') {
      return { state: 'active', label: 'Active' };
    }
    return { state: 'queued', label: 'Queued' };
  }
  if (isRunning) {
    return { state: 'running', label: 'Running' };
  }
  if (status === 'in_progress') {
    return { state: 'running', label: 'Running' };
  }
  return { state: 'queued', label: 'Queued' };
}

function buildAwaitingInputDetails(step, state) {
  if (!step) {
    return null;
  }
  const pending = step.executionState?.pendingSelection ?? null;
  const awaitingSelection = step.selection === 'ask' && !step.selectedActionId;
  const isAwaiting = state?.label === 'Awaiting';
  const isBlockedWithPending = state?.state === 'blocked' && Boolean(pending);
  if (!isAwaiting && !isBlockedWithPending) {
    return null;
  }
  if (!pending && !awaitingSelection) {
    return null;
  }
  const title =
    step.name ??
    step.label ??
    step.metadata?.label ??
    step.stepId ??
    step.id ??
    'Awaiting input';
  const lines = [];
  if (pending?.instructions) {
    lines.push(pending.instructions);
  }
  if (Array.isArray(pending?.metadata?.missingParams) && pending.metadata.missingParams.length) {
    lines.push(`Missing parameters: ${pending.metadata.missingParams.join(', ')}.`);
  }
  if (pending?.query && pending.query !== pending.instructions) {
    lines.push(pending.query);
  }
  if (!lines.length && awaitingSelection) {
    lines.push('Awaiting your selection.');
  }
  return { title, lines };
}

function formatElapsedTime(ms) {
  if (!Number.isFinite(ms) || ms <= 0) {
    return null;
  }
  const totalSeconds = Math.floor(ms / 1000);
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);
  const parts = [];
  if (hours) parts.push(`${hours}h`);
  if (minutes || hours) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);
  return parts.join(' ');
}

function resolveStepDurationLabel(step, state) {
  if (!step) {
    return null;
  }
  const exec = step.executionState ?? {};
  const runner = exec.runner ?? {};
  const parseMs = (value) => {
    if (!value) return null;
    const ts = new Date(value).getTime();
    return Number.isFinite(ts) ? ts : null;
  };
  const startedAt =
    runner.startedAt ??
    runner.started ??
    runner.queuedAt ??
    exec.startedAt ??
    exec.started ??
    exec.queuedAt ??
    exec.lastRunAt ??
    step?.updatedAt ??
    null;
  const completedAt =
    runner.completedAt ??
    runner.completed ??
    runner.finishedAt ??
    runner.finished ??
    runner.endedAt ??
    exec.completedAt ??
    exec.completed ??
    exec.updatedAt ??
    step?.updatedAt ??
    null;
  const explicitDurationMs =
    exec.durationMs ??
    runner.durationMs ??
    null;
  if (state === 'running' && startedAt) {
    const elapsed = formatElapsedTime(Date.now() - new Date(startedAt).getTime());
    return elapsed ? { label: elapsed, isRunning: true } : null;
  }
  if (state === 'done' || state === 'skipped' || state === 'blocked') {
    const durationCandidates = [
      [runner.startedAt ?? runner.started, runner.completedAt ?? runner.completed ?? runner.finishedAt ?? runner.finished ?? runner.endedAt],
      [runner.queuedAt, runner.completedAt ?? runner.completed ?? runner.finishedAt ?? runner.finished ?? runner.endedAt],
      [exec.startedAt ?? exec.started, exec.completedAt ?? exec.completed],
      [exec.lastRunAt ?? exec.queuedAt, exec.completedAt ?? exec.completed ?? exec.updatedAt],
      [startedAt, completedAt],
    ];
    for (const [startCandidate, endCandidate] of durationCandidates) {
      const startMs = parseMs(startCandidate);
      const endMs = parseMs(endCandidate);
      if (startMs !== null && endMs !== null && endMs > startMs) {
        const elapsed = formatElapsedTime(endMs - startMs);
        if (elapsed) {
          return { label: elapsed, isRunning: false };
        }
      }
    }
  }
  if ((state === 'done' || state === 'skipped' || state === 'blocked') && Number.isFinite(explicitDurationMs)) {
    const elapsed = formatElapsedTime(explicitDurationMs);
    return elapsed ? { label: elapsed, isRunning: false } : null;
  }
  return null;
}

function resolvePlanStepActionVisibility(step, state) {
  if (!step) {
    return { showFocus: false, showRetry: false, showSkip: false, showDelete: false };
  }
  const origin = resolveStepOrigin(step);
  const status = (step?.runtime?.status ?? '').toLowerCase();
  const execStatusRaw = step?.executionState?.status ?? '';
  const runnerStatusRaw = step?.executionState?.runner?.status ?? '';
  const execStatus = typeof execStatusRaw === 'string' ? execStatusRaw.toLowerCase() : '';
  const runnerStatus = typeof runnerStatusRaw === 'string' ? runnerStatusRaw.toLowerCase() : '';
  const isRunning = state.state === 'running';
  const isDone = state.state === 'done';
  const isSkipped = state.state === 'skipped';
  const isBlocked = state.state === 'blocked';
  const isFailed =
    ['failed', 'error'].includes(execStatus) ||
    ['failed', 'error'].includes(runnerStatus) ||
    ['failed', 'error'].includes(status);

  return {
    showFocus: !isRunning && !isDone,
    showRetry: isFailed || isBlocked || isSkipped || isDone,
    showSkip: !isRunning && !isDone && !isSkipped,
    showDelete: origin !== 'core' && !isRunning,
  };
}

const BRIGHTDATA_TERMINAL_STATUSES = new Set([
  'collected',
  'complete',
  'completed',
  'failed',
  'failed to trigger',
  'error',
  'cancelled',
  'canceled',
]);

function extractBrightdataStatus(entry) {
  if (!entry) {
    return null;
  }
  if (typeof entry === 'string') {
    return entry;
  }
  if (typeof entry.status === 'string') {
    return entry.status;
  }
  if (typeof entry.status?.status === 'string') {
    return entry.status.status;
  }
  if (typeof entry.status?.text === 'string') {
    return entry.status.text;
  }
  if (typeof entry.text === 'string') {
    return entry.text;
  }
  if (typeof entry.message === 'string') {
    return entry.message;
  }
  return null;
}

function isBrightdataActiveEntry(entry) {
  if (!entry) {
    return false;
  }
  const statusValue = extractBrightdataStatus(entry);
  if (!statusValue) {
    return true;
  }
  const normalized = statusValue.toLowerCase();
  return !BRIGHTDATA_TERMINAL_STATUSES.has(normalized);
}

function collectPlanStepPrimitiveIds(step) {
  const ids = new Set();
  const addId = (value) => {
    if (typeof value === 'string' && value.trim()) {
      ids.add(value);
    }
  };
  addId(step?.primitiveId);
  addId(step?.executionState?.primaryPrimitiveId);
  addId(step?.executionState?.peerManualSegmentId);
  addId(step?.executionState?.peerFullListViewId);
  addId(step?.executionState?.peerQueryId);
  addId(step?.executionState?.runner?.targetPrimitiveId);
  const runnerContext = step?.executionState?.runner?.context ?? null;
  addId(runnerContext?.targetPrimitiveId);
  addId(runnerContext?.preferredPrimaryId);
  addId(runnerContext?.queryId);
  return Array.from(ids);
}

function collectBrightdataCandidates(mainstore, step) {
  if (!mainstore || !step) {
    return [];
  }
  const primitiveIds = collectPlanStepPrimitiveIds(step);
  const candidates = new Map();
  const enqueue = (primitive) => {
    if (!primitive || !primitive.id) {
      return;
    }
    if (candidates.has(primitive.id)) {
      return;
    }
    candidates.set(primitive.id, primitive);
  };
  primitiveIds.forEach((id) => {
    const primitive = mainstore.primitive(id);
    if (!primitive) {
      return;
    }
    enqueue(primitive);
    const relatedIds = getPlanPrimitiveRelationshipIds(primitive);
    relatedIds.forEach((relatedId) => {
      const related = mainstore.primitive(relatedId);
      if (related) {
        enqueue(related);
      }
    });
    const originItems = primitive.primitives?.origin?.allItems ?? [];
    const originSearch = primitive.primitives?.origin?.allSearch ?? [];
    const configSearch = primitive.primitives?.config?.allSearch ?? [];
    const inputSearch = primitive.primitives?.inputs?.allSearch ?? [];
    const importSearch = primitive.primitives?.imports?.allSearch ?? [];
    originItems.forEach(enqueue);
    originSearch.forEach(enqueue);
    configSearch.forEach(enqueue);
    inputSearch.forEach(enqueue);
    importSearch.forEach(enqueue);
  });
  return Array.from(candidates.values());
}

function hasBrightdataProcessing(mainstore, step) {
  const candidates = collectBrightdataCandidates(mainstore, step);
  for (const primitive of candidates) {
    const bd = primitive?.processing?.bd ?? null;
    if (!bd) {
      continue;
    }
    const entries = Object.values(bd);
    for (const entry of entries) {
      if (isBrightdataActiveEntry(entry)) {
        return true;
      }
    }
  }
  return false;
}

function resolveAdvisorTypeLabel(type) {
  if (type === 'gap_fill_request') {
    return 'Data needed';
  }
  if (type === 'analysis_ready') {
    return 'Analysis ready';
  }
  if (type === 'visualization_ready') {
    return 'Visualization ready';
  }
  return 'Advisor';
}

function extractAutoStepItems(summary = {}) {
  if (!summary) {
    return [];
  }
  if (Array.isArray(summary.peers)) {
    return summary.peers;
  }
  if (Array.isArray(summary.items)) {
    return summary.items;
  }
  if (Array.isArray(summary.entities)) {
    return summary.entities;
  }
  return [];
}

function formatAutoStepPrompt(signal) {
  if (!signal) {
    return "";
  }
  const lines = [];
  const tokenLabel = signal.stepId ?? signal.token ?? "auto_step";
  lines.push(`[[auto_step:${tokenLabel}]]`);
  if (signal.stepName) {
    lines.push(`Automated step "${signal.stepName}" just finished.`);
  } else {
    lines.push("An automated task step just finished.");
  }
  if (signal.summary?.message) {
    lines.push(signal.summary.message);
  }
  const items = extractAutoStepItems(signal.summary);
  if (items.length) {
    lines.push("Key findings:");
    const displayItems = items.slice(0, 8);
    displayItems.forEach((item, idx) => {
      const label =
        item?.name ||
        item?.title ||
        item?.label ||
        item?.metadata?.name ||
        item?.entityId ||
        `Item ${idx + 1}`;
      lines.push(`${idx + 1}. ${label}`);
    });
    if (items.length > displayItems.length) {
      lines.push(`...and ${items.length - displayItems.length} more.`);
    }
  }
  lines.push(
    "Summarize these results for the user, highlight any follow-ups, and recommend next steps."
  );
  lines.push(
    "Respond with one or two concise sentences that capture the main findings and the next recommended action. No filler, no follow-up questions, and no control tokens."
  );
  return lines.filter(Boolean).join("\n");
}

function buildAutoStepMessage(signal) {
  if (!signal) {
    return null;
  }
  const content = formatAutoStepPrompt(signal);
  if (!content) {
    return null;
  }
  return {
    role: "user",
    content,
    hidden: true,
    metadata: {
      autoStep: {
        token: signal.token ?? null,
        type: signal.summary?.type ?? signal.actionId ?? null,
        stepId: signal.stepId ?? null,
      },
    },
  };
}

function resolveChatUpdatedAtMs(chat) {
  const raw =
    chat?.updatedAt ??
    chat?.updated_at ??
    chat?.lastUpdatedAt ??
    chat?.createdAt ??
    chat?.created_at ??
    0;
  if (typeof raw === "number") {
    return raw;
  }
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
}


function normalizePlanStepMetadata(metadata) {
  if (!metadata || typeof metadata !== 'object') {
    return null;
  }
  const normalized = { ...metadata };
  if (normalized.source_step_id && !normalized.sourceStepId) {
    normalized.sourceStepId = normalized.source_step_id;
  }
  if (normalized.parent_step_id && !normalized.parentStepId) {
    normalized.parentStepId = normalized.parent_step_id;
  }
  if (normalized.sub_chat && !normalized.subChat) {
    normalized.subChat = normalized.sub_chat;
  }
  if (normalized.requested_by_step_id && !normalized.requestedByStepId) {
    normalized.requestedByStepId = normalized.requested_by_step_id;
  }
  return normalized;
}

function mergePlanStepMetadata(primary, secondary) {
  const normalizedSecondary = normalizePlanStepMetadata(secondary);
  const normalizedPrimary = normalizePlanStepMetadata(primary);
  if (normalizedSecondary && normalizedPrimary) {
    return { ...normalizedSecondary, ...normalizedPrimary };
  }
  return normalizedPrimary ?? normalizedSecondary ?? null;
}

function collapseSubChatSummaries(messages) {
  if (!Array.isArray(messages)) {
    return [];
  }
  if (messages.length < 2) {
    return messages;
  }
  let modified = false;
  const result = [];
  for (let idx = 0; idx < messages.length; idx += 1) {
    const message = messages[idx];
    if (message?.metadata?.subChatSummary && message.metadata?.sourceChatId) {
      let cursor = result.length - 1;
      while (cursor >= 0) {
        const candidate = result[cursor];
        if (!candidate) {
          cursor -= 1;
          continue;
        }
        if (candidate.hidden) {
          cursor -= 1;
          continue;
        }
        const candidateIsSummary = Boolean(candidate.metadata?.subChatSummary);
        if (!candidateIsSummary) {
          break;
        }
        if (candidate.metadata?.sourceChatId === message.metadata.sourceChatId) {
          result.splice(cursor, 1);
          modified = true;
        }
        break;
      }
    }
    result.push(message);
  }
  return modified ? result : messages;
}

function collectIdsFromRelationshipContainer(container) {
  if (!container) {
    return [];
  }
  const ids = new Set();
  const addFrom = (value) => {
    if (!value) {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        const id = typeof entry === 'object' ? entry?.id : entry;
        if (id) {
          ids.add(id);
        }
      });
      return;
    }
    if (typeof value.forEach === 'function') {
      try {
        value.forEach((entry) => {
          const id = typeof entry === 'object' ? entry?.id : entry;
          if (id) {
            ids.add(id);
          }
        });
        return;
      } catch (error) {
        // iterable threw; ignore and continue
      }
    }
    if (typeof value[Symbol.iterator] === 'function' && typeof value !== 'string') {
      try {
        for (const entry of value) {
          const id = typeof entry === 'object' ? entry?.id : entry;
          if (id) {
            ids.add(id);
          }
        }
        return;
      } catch (error) {
        // non-iterable proxy, skip
      }
    }
    if (typeof value === 'object') {
      if (value.id) {
        ids.add(value.id);
      }
      return;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      ids.add(value);
    }
  };

  addFrom(container.allItems);
  addFrom(container.allIds);
  addFrom(container.uniqueAllItems);
  addFrom(container.uniqueAllIds);
  addFrom(container.ids);

  let keys = [];
  try {
    keys = Object.keys(container);
  } catch (error) {
    keys = [];
  }
  keys.forEach((key) => {
    if (['allItems', 'uniqueAllItems', 'allIds', 'uniqueAllIds', 'ids', 'paths', 'length'].includes(key)) {
      return;
    }
    const value = container[key];
    if (value && typeof value === 'object') {
      addFrom(value.allItems);
      addFrom(value.allIds);
    }
  });

  return Array.from(ids);
}

function getPlanPrimitiveRelationshipIds(
  primitive,
  relationshipKeys = ['inputs', 'imports', 'origin', 'auto', 'link', 'config', 'outputs']
) {
  if (!primitive) {
    return [];
  }
  const collected = new Set();
  relationshipKeys.forEach((key) => {
    let relationship = null;
    try {
      relationship = primitive.primitives?.[key];
    } catch (error) {
      relationship = null;
    }
    if (!relationship) {
      return;
    }
    collectIdsFromRelationshipContainer(relationship).forEach((id) => {
      if (id) {
        collected.add(id);
      }
    });
  });
  return Array.from(collected);
}

function extractPlanFromPrimitive(planPrimitive) {
  if (!planPrimitive) {
    return null;
  }

  const planRef = planPrimitive.referenceParameters ?? {};
  const targetRecipeId =
    typeof planRef.recipeId === 'string' && planRef.recipeId.trim().length
      ? planRef.recipeId.trim()
      : null;
  const children = planPrimitive.primitives?.origin?.allItems ?? [];
  const allStepPrimitives = children.filter((child) => child?.type === 'step');
  const recipeStepPrimitives = allStepPrimitives.filter((child) => {
    if (!targetRecipeId) {
      return true;
    }
    const stepRecipeId =
      typeof child?.referenceParameters?.recipeId === 'string' &&
      child.referenceParameters.recipeId.trim().length
        ? child.referenceParameters.recipeId.trim()
        : null;
    if (!stepRecipeId) {
      return true;
    }
    return stepRecipeId === targetRecipeId;
  });
  const serializedPlanSteps = Array.isArray(planRef.plan_steps) ? planRef.plan_steps : [];
  const baseSteps = serializedPlanSteps.map((step, idx) => {
    const order = Number.isFinite(step.order) ? step.order : idx;
    const normalizedInputs = Array.isArray(step.inputs)
      ? step.inputs
          .map((input) => {
            if (!input) {
              return null;
            }
            return {
              id: input.id ?? null,
              stepId: input.stepId ?? input.step_id ?? null,
              description: input.description ?? null,
            };
          })
          .filter((input) => input?.stepId)
      : [];
    const metadata = mergePlanStepMetadata(step.metadata, null);
    return {
      id: step.id ?? step.stepId ?? `plan_step_${idx}`,
      primitiveId: null,
      runtime: step.runtime ?? null,
      name: step.name ?? `Step ${order + 1}`,
      description: step.description ?? '',
      area: step.area ?? 'discovery',
      selection: step.selection ?? null,
      optional: Boolean(step.optional),
      mode: step.mode ?? null,
      order,
      executionState: step.executionState ?? null,
      inputs: normalizedInputs,
      metadata: metadata ?? null,
    };
  });

  const stepMap = new Map();
  baseSteps.forEach((step, idx) => {
    const key = step.id ?? `plan_step_${idx}`;
    stepMap.set(key, step);
  });

  const hasCanonicalSerializedSteps = serializedPlanSteps.length > 0;
  recipeStepPrimitives.forEach((child, idx) => {
    const ref = child.referenceParameters ?? {};
    const order = Number.isFinite(ref.order) ? ref.order : idx;
    const candidateKeys = [ref.stepId, ref.id, child.id].filter(Boolean);
    let matchedKey = candidateKeys.find((candidate) => stepMap.has(candidate)) ?? null;
    if (!matchedKey && hasCanonicalSerializedSteps) {
      // Fallback to canonical step order when step ids are missing on step primitives.
      const orderedFallback = Array.from(stepMap.entries()).find(([, existingStep]) => {
        if (!existingStep || existingStep.primitiveId) {
          return false;
        }
        return Number.isFinite(existingStep.order) && existingStep.order === order;
      });
      matchedKey = orderedFallback?.[0] ?? null;
    }
    if (hasCanonicalSerializedSteps && !matchedKey) {
      // Canonical plan_steps is authoritative; ignore orphan step primitives
      // that can linger from previously active recipes/plans.
      return;
    }
    const key = matchedKey ?? candidateKeys[0] ?? `artifact_${idx}`;
    const existing = stepMap.get(key);
    const mergedInputs = Array.isArray(existing?.inputs) && existing.inputs.length
      ? existing.inputs
      : Array.isArray(ref.inputs)
        ? ref.inputs
            .map((input) => {
              if (!input) {
                return null;
              }
              return {
                id: input.id ?? null,
                stepId: input.stepId ?? input.step_id ?? null,
                description: input.description ?? null,
              };
            })
            .filter((input) => input?.stepId)
        : [];
    const metadata = mergePlanStepMetadata(existing?.metadata, ref.metadata);
    const merged = {
      ...(existing || {}),
      id: ref.stepId ?? existing?.id ?? child.id,
      primitiveId: child.id,
      runtime: ref.runtime ?? existing?.runtime ?? null,
      name: existing?.name ?? ref.name ?? child.title ?? `Step ${order + 1}`,
      description: existing?.description ?? ref.description ?? '',
      area: existing?.area ?? ref.area ?? 'discovery',
      selection: existing?.selection ?? ref.selection ?? null,
      optional: typeof (existing?.optional ?? ref.optional) === 'boolean'
        ? existing?.optional ?? ref.optional
        : false,
      mode: existing?.mode ?? ref.mode ?? null,
      order,
      executionState: ref.executionState ?? existing?.executionState ?? null,
      inputs: mergedInputs,
      metadata: metadata ?? existing?.metadata ?? null,
    };
    stepMap.set(key, merged);
  });

  let steps = Array.from(stepMap.values());
  if (!steps.length) {
    steps = recipeStepPrimitives.map((child, idx) => {
      const ref = child.referenceParameters ?? {};
      const order = Number.isFinite(ref.order) ? ref.order : idx;
      return {
        id: ref.stepId ?? child.id,
        primitiveId: child.id,
        runtime: ref.runtime ?? null,
        name: ref.name ?? child.title ?? `Step ${order + 1}`,
        description: ref.description ?? '',
        area: ref.area ?? 'discovery',
        selection: ref.selection ?? null,
        optional: Boolean(ref.optional),
        mode: ref.mode ?? null,
        order,
        executionState: ref.executionState ?? null,
        inputs: Array.isArray(ref.inputs)
          ? ref.inputs
              .map((input) => ({
                id: input.id ?? null,
                stepId: input.stepId ?? input.step_id ?? null,
                description: input.description ?? null,
              }))
              .filter((input) => input?.stepId)
          : [],
        metadata: normalizePlanStepMetadata(ref.metadata) ?? null,
      };
    });
  }

  steps = steps
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((step, idx) => ({ ...step, order: idx }));

  return {
    source: 'artifact',
    planPrimitiveId: planPrimitive.id ?? planPrimitive._id ?? null,
    title: planPrimitive.title ?? planRef.title ?? 'Task plan',
    summary: planRef.summary ?? '',
    recipeId: planRef.recipeId ?? null,
    focus: planRef.focus ?? null,
    area: planRef.area ?? null,
    status: planRef.status ?? 'pending',
    goals: Array.isArray(planRef.goals) ? planRef.goals : [],
    pendingGoalPlan: planRef.pendingGoalPlan ?? null,
    activeGoalId: planRef.activeGoalId ?? null,
    activeStepKey: planRef.activeStepKey ?? null,
    runtimeVersion: Number.isFinite(planRef.runtimeVersion) ? planRef.runtimeVersion : null,
    activeStepId:
      planRef.activeStepId ??
      steps.find((step) => (step?.runtime?.status ?? '') === 'in_progress')?.primitiveId ??
      null,
    steps,
    updatedAt: planRef.updatedAt ?? planPrimitive.updatedAt ?? planPrimitive.updated_at ?? null,
  };
}

function sanitizeChatHistory(messages) {
  if (!Array.isArray(messages)) {
    return [];
  }

  const limit = 500;
  const trimmed = messages.slice(-limit);

  return trimmed
    .map((rawEntry) => {
      const entry = compactHistoryEntry(rawEntry);
      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const safe = {
        role: typeof entry.role === 'string' ? entry.role : 'assistant',
        content: typeof entry.content === 'string' ? entry.content : '',
      };

      if (entry.hidden) {
        safe.hidden = true;
      }

      if (entry.preview) {
        safe.preview = true;
      }

      if (entry.context && typeof entry.context === 'object') {
        const context = { ...entry.context };
        if (context.goalPlan && typeof context.goalPlan === 'object') {
          const ref = {
            type: 'goal_plan_ref',
            goalPlanId: context.goalPlan.id ?? null,
            status: context.goalPlan.status ?? 'pending',
            goalIds: Array.isArray(context.goalPlan.goals)
              ? context.goalPlan.goals.map((goal) => goal?.id).filter(Boolean)
              : [],
            createdAt: context.goalPlan.createdAt ?? null,
          };
          delete context.goalPlan;
          context.goalPlanRef = ref;
        }
        safe.context = context;
      }

      if (entry.resultFor && typeof entry.resultFor === 'string') {
        safe.resultFor = entry.resultFor;
      }

      if (entry.name && typeof entry.name === 'string') {
        safe.name = entry.name;
      }

      if (entry.badgeType && typeof entry.badgeType === 'string') {
        safe.badgeType = entry.badgeType;
      }

      if (entry.badgeData && typeof entry.badgeData === 'object') {
        try {
          safe.badgeData = JSON.parse(JSON.stringify(entry.badgeData));
        } catch (error) {
          safe.badgeData = { ...entry.badgeData };
        }
      }

      if (entry.metadata && typeof entry.metadata === 'object' && Object.keys(entry.metadata).length > 0) {
        try {
          safe.metadata = JSON.parse(JSON.stringify(entry.metadata));
        } catch (error) {
          safe.metadata = { ...entry.metadata };
        }
      }

      return safe;
    })
    .filter(Boolean);
}

function getPrimitiveId(primitive) {
  return primitive?.id ?? primitive?._id ?? null;
}

//export default function AgentChat({primitive, ...props}) {
function ChatSessionToolbar({
  availableChats,
  activeChatId,
  activeUpdatedAt,
  pending,
  chatLoading,
  onSelect,
  onCreate,
  onDelete,
  hideSessionControls,
  hideUpdatedTimestamp,
}) {
  if (hideSessionControls) {
    return hideUpdatedTimestamp || !activeUpdatedAt ? null : (
      <div className="mb-3 text-xs text-slate-500">
        Updated {new Date(activeUpdatedAt).toLocaleString()}
      </div>
    );
  }
  const hasChats = (availableChats ?? []).length > 0;
  return (
    <div className="w-full flex flex-col gap-2 mb-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs font-medium text-slate-500" htmlFor="agent-chat-session">
          Session
        </label>
        <select
          id="agent-chat-session"
          value={activeChatId ?? ''}
          onChange={(event) => onSelect?.(event.target.value || null)}
          disabled={chatLoading || pending}
          className="rounded-md border border-slate-200 bg-white px-2 py-1 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
        >
          <option value="" disabled={!hasChats}>
            {hasChats ? 'Select chat session' : 'No chats available'}
          </option>
          {availableChats.map((chat) => {
            const chatId = chat?.id ?? chat?._id;
            const title = chat?.title ?? `Chat ${chat?.plainId ?? ''}`;
            return (
              <option key={chatId} value={chatId}>
                {title}
              </option>
            );
          })}
        </select>
        <Button
          variant="light"
          size="sm"
          onPress={onCreate}
          isDisabled={chatLoading || pending}
          className="gap-1"
        >
          <HeroIcon icon="PlusCircleIcon" className="h-4 w-4" />
          New chat
        </Button>
        <Button
          variant="light"
          color="danger"
          size="sm"
          onPress={onDelete}
          isDisabled={chatLoading || pending || !activeChatId}
          className="gap-1"
        >
          <HeroIcon icon="TrashIcon" className="h-4 w-4" />
          Delete
        </Button>
      </div>
      {activeUpdatedAt && !hideUpdatedTimestamp && (
        <span className="text-xs text-slate-500">
          Updated {new Date(activeUpdatedAt).toLocaleString()}
        </span>
      )}
    </div>
  );
}

const AgentChat = forwardRef(function AgentChat({
  primitive,
  scope: agentScope,
  chatMode,
  onTaskAreaChange,
  onPlanPrimitiveChange,
  hideChatSession,
  hideChatUpdatedTimestamp,
  onAdvisorSuggestionsChange,
  onChatSessionChange,
  ...props
}, ref){
      const mainstore = useMemo(() => MainStore(), []);
      const [messages, setMessagesState] = useState([


        
        /*{
    "hidden": false,
    "role": "assistant",
    "content": "Absolutely! Here are a few additional ideas for visualizations using Reddit data to complement your current Instagram-focused slide:\n\n1. **Sentiment Comparison Chart:**  \n   Visualize the overall sentiment (positive, neutral, negative) of Reddit discussions about Thorne’s purchase drivers and barriers. This could be a stacked bar or pie chart, showing whether Reddit users are more skeptical, enthusiastic, or mixed compared to Instagram.\n\n2. **Theme Heatmap:**  \n   Create a heatmap showing which specific product attributes (e.g., ingredient transparency, clinical evidence, price, taste) are discussed most frequently on Reddit versus Instagram. This would highlight where conversations overlap and where they diverge.\n\n3. **Quote Cloud or Highlighted Comments:**  \n   Feature a “voice of the customer” section with representative Reddit quotes for each major purchase driver or barrier. This adds qualitative depth and shows how Reddit users articulate their reasoning.\n\n4. **Journey Mapping:**  \n   Map out the typical “wellness journey” or decision process as described by Reddit users—what triggers their interest, what research they do, what convinces or deters them. This could be a flow diagram or annotated timeline.\n\n5. **Influencer vs. Peer Signal:**  \n   Compare the influence of peer recommendations versus influencer/celebrity mentions on Reddit (which tends to be more peer-driven) versus Instagram. This could be a side-by-side bar chart or a network diagram.\n\n6. **Barrier Deep Dive:**  \n   Use Reddit data to do a deeper dive into a specific barrier (e.g., skepticism about efficacy or price concerns), showing sub-themes or the most common questions/complaints.\n\nIf any of these ideas interest you, let me know which one(s) you’d like to explore further!"
}*/
        /*{
            "hidden": false,
            "role": "assistant",
            "content": "{\"views\":[{\"source\":\"68825b3088d71b23808e4f6e\",\"title\":\"Distribution of Influencers by Follower Count Category\",\"layout\":\"pie\",\"x_axis\":{\"parameter\":\"followers\"}}]}",
            "resultFor": "design_view",
            "preview": true
        }*/
        /*{
            "role": "assistant",
            "content": `"### Efficacy
- Consumer reviews report noticeable improvements in joint and ligament pain relief.
- Some users experienced marked reductions in pain after using the supplements.
- There are mixed experiences with some users reporting worsening symptoms due to dosing issues.
- Positive endorsements include both consumer and healthcare professional observations.
### Ingredient Transparency
- Thorne states that all supplements are free of gluten, artificial fillers, dyes, and additives.
- Ingredients are sourced from trusted suppliers, with in-house laboratory testing for quality control.
- Only select products are independently tested, such as those carrying NSF Certified for Sport.
### Concerns and Praise
## Concerns
- Some reviews highlight that not all products are third-party tested.
- Several users raise concerns about the serving size, noting that 4 capsules can be too many at one time.
- There are reports of quality issues, including incidents of receiving empty capsules.
- A few consumers mentioned worsening joint symptoms over time.
## Praise
- Numerous customer reviews praise the supplements for effective joint and ligament support.
- Positive feedback includes noticeable improvements in pain and overall joint function.
- Endorsements from users and healthcare professionals reinforce claims of efficacy.
- Several consumers stated that the supplements performed better than other brands. [[ref:688229695df2b9a82e1e36a4,688229705df2b9a82e1e36b9,688229765df2b9a82e1e36ce,6882293e5df2b9a82e1e3647]]`
         
        }*/
      ]);
      const setMessages = useCallback((value) => {
        if (typeof value === 'function') {
          setMessagesState((prev) => collapseSubChatSummaries(value(prev)));
          return;
        }
        setMessagesState(collapseSubChatSummaries(value));
      }, [setMessagesState]);
      const inputBox = useRef({})
      const timelineRef = useRef(null);
      const [autoSend, setAutoSend] = useState('');
      const [pending, setPending] = useState(false);
      const [context, setContext] = useState(false);
      const [chatState, setChatState] = useState({});
      const [taskState, setTaskState] = useState(null);
      const [planArtifacts, setPlanArtifacts] = useState(null);
      const [showGoalDeleteConfirm, setShowGoalDeleteConfirm] = useState(false);
      const [goalToDelete, setGoalToDelete] = useState(null);
      const [deletingGoal, setDeletingGoal] = useState(false);
      const [collapsedSubplanState, setCollapsedSubplanState] = useState({});
      const [advisorSuggestions, setAdvisorSuggestions] = useState([]);
      const advisorSuggestionMap = useMemo(() => {
        const map = new Map();
        advisorSuggestions.forEach((suggestion) => {
          if (suggestion?.id) {
            map.set(suggestion.id, suggestion);
          }
        });
        return map;
      }, [advisorSuggestions]);
      const visibleAdvisorSuggestions = useMemo(
        () => advisorSuggestions.slice(0, MAX_ADVISOR_SUGGESTIONS),
        [advisorSuggestions]
      );
      const visibleAdvisorSuggestionIds = useMemo(() => {
        return new Set(visibleAdvisorSuggestions.map((entry) => entry?.id).filter(Boolean));
      }, [visibleAdvisorSuggestions]);
      const advisorSuggestionNumberLookup = useMemo(() => {
        const idToNumber = new Map();
        const numberToId = new Map();
        visibleAdvisorSuggestions.forEach((entry, idx) => {
          if (!entry?.id) {
            return;
          }
          const number = idx + 1;
          idToNumber.set(entry.id, number);
          numberToId.set(number, entry.id);
        });
        return { idToNumber, numberToId };
      }, [visibleAdvisorSuggestions]);
      const advisorLabelById = useMemo(() => {
        const map = new Map();
        messages.forEach((entry) => {
          if (!entry) {
            return;
          }
          const suggestionId = entry?.metadata?.advisorSuggestionId;
          if (!suggestionId) {
            return;
          }
          if (map.has(suggestionId)) {
            return;
          }
          const payload = entry?.metadata?.advisorSuggestionPayload ?? null;
          const label =
            payload?.label ??
            payload?.message ??
            entry?.badgeData?.message ??
            entry?.badgeData?.label ??
            null;
          if (label) {
            map.set(suggestionId, label);
          }
        });
        advisorSuggestionMap.forEach((entry, id) => {
          if (!map.has(id)) {
            const label = entry?.label ?? entry?.message ?? entry?.optionId ?? null;
            if (label) {
              map.set(id, label);
            }
          }
        });
        return map;
      }, [messages, advisorSuggestionMap]);
      const goalPlanMetaByGoalId = useMemo(() => {
        const map = new Map();
        messages.forEach((entry, idx) => {
          if (!entry || entry.badgeType !== 'goal_plan') {
            return;
          }
          const goalPlanId = resolveGoalPlanIdFromEntry(entry);
          const planLabel = entry?.badgeData?.plan?.goalText ?? null;
          const goals = Array.isArray(entry?.badgeData?.plan?.goals) ? entry.badgeData.plan.goals : [];
          goals.forEach((goal) => {
            const goalId = goal?.id ?? null;
            if (!goalId) {
              return;
            }
            map.set(goalId, {
              label: goal.label ?? null,
              planLabel,
              goalPlanId,
              sourceIndex: idx,
            });
          });
          const compactGoalIds = [
            ...(Array.isArray(entry?.badgeData?.planRef?.goalIds) ? entry.badgeData.planRef.goalIds : []),
            ...(Array.isArray(entry?.metadata?.goalPlanGoalIds) ? entry.metadata.goalPlanGoalIds : []),
          ]
            .map((goalId) => (typeof goalId === 'string' ? goalId : null))
            .filter(Boolean);
          compactGoalIds.forEach((goalId) => {
            map.set(goalId, {
              label: null,
              planLabel,
              goalPlanId,
              sourceIndex: idx,
            });
          });
        });
        return map;
      }, [messages]);
      const goalPlanMetaByQueueGroupId = useMemo(() => {
        const map = new Map();
        messages.forEach((entry, idx) => {
          if (!entry || entry.badgeType !== 'goal_plan') {
            return;
          }
          const goalPlanId = resolveGoalPlanIdFromEntry(entry);
          const planLabel = entry?.badgeData?.plan?.goalText ?? null;
          const goals = Array.isArray(entry?.badgeData?.plan?.goals) ? entry.badgeData.plan.goals : [];
          goals.forEach((goal) => {
            const queueGroupId = goal?.queueGroupId ?? null;
            if (!queueGroupId) {
              return;
            }
            const rawQueueGroupId = String(queueGroupId);
            const queueGroupKeyVariants = new Set([rawQueueGroupId]);
            queueGroupKeyVariants.add(`goalq_${rawQueueGroupId}`);
            if (goalPlanId && !rawQueueGroupId.includes('::')) {
              queueGroupKeyVariants.add(`goalq_${goalPlanId}::${rawQueueGroupId}`);
            }
            const payload = {
              label: goal.label ?? null,
              planLabel,
              goalPlanId,
              sourceIndex: idx,
            };
            queueGroupKeyVariants.forEach((key) => map.set(key, payload));
          });
        });
        return map;
      }, [messages]);
      const subplanInsertIndexMap = useMemo(() => {
        const map = new Map();
        let latestGoalPlanIndex = null;
        let earliestGoalPlanIndex = null;
        let latestAcceptedGoalPlanIndex = null;
        let earliestAcceptedGoalPlanIndex = null;
        let latestPendingGoalPlanIndex = null;
        let earliestPendingGoalPlanIndex = null;
        let latestDeclinedGoalPlanIndex = null;
        let earliestDeclinedGoalPlanIndex = null;
        messages.forEach((entry, idx) => {
          if (!entry) {
            return;
          }
          if (entry.badgeType === 'advisor' && entry?.metadata?.advisorSuggestionId) {
            map.set(`advisor_${entry.metadata.advisorSuggestionId}`, idx);
          }
          if (entry.badgeType === 'goal_plan') {
            const goalPlanId = resolveGoalPlanIdFromEntry(entry);
            if (goalPlanId) {
              map.set(`goalplan_${goalPlanId}`, idx);
            }
            latestGoalPlanIndex = idx;
            if (earliestGoalPlanIndex === null) {
              earliestGoalPlanIndex = idx;
            }
            const status = entry?.badgeData?.status ?? entry?.metadata?.goalPlanStatus ?? null;
            if (status === 'accepted') {
              latestAcceptedGoalPlanIndex = idx;
              if (earliestAcceptedGoalPlanIndex === null) {
                earliestAcceptedGoalPlanIndex = idx;
              }
            } else if (status === 'pending') {
              latestPendingGoalPlanIndex = idx;
              if (earliestPendingGoalPlanIndex === null) {
                earliestPendingGoalPlanIndex = idx;
              }
            } else if (status === 'declined') {
              latestDeclinedGoalPlanIndex = idx;
              if (earliestDeclinedGoalPlanIndex === null) {
                earliestDeclinedGoalPlanIndex = idx;
              }
            }
            const planGoalIds = Array.isArray(entry?.badgeData?.plan?.goals)
              ? entry.badgeData.plan.goals.map((goal) => goal?.id).filter(Boolean)
              : [];
            const planQueueGroupIds = Array.isArray(entry?.badgeData?.plan?.goals)
              ? entry.badgeData.plan.goals.map((goal) => goal?.queueGroupId).filter(Boolean)
              : [];
            const compactGoalIds = [
              ...(Array.isArray(entry?.badgeData?.planRef?.goalIds) ? entry.badgeData.planRef.goalIds : []),
              ...(Array.isArray(entry?.metadata?.goalPlanGoalIds) ? entry.metadata.goalPlanGoalIds : []),
            ]
              .map((goalId) => (typeof goalId === 'string' ? goalId : null))
              .filter(Boolean);
            planQueueGroupIds.forEach((queueGroupId) => {
              const rawQueueGroupId = String(queueGroupId);
              const keyVariants = new Set([`goalq_${rawQueueGroupId}`]);
              if (goalPlanId && !rawQueueGroupId.includes('::')) {
                keyVariants.add(`goalq_${goalPlanId}::${rawQueueGroupId}`);
              }
              keyVariants.forEach((key) => {
                if (!map.has(key)) {
                  map.set(key, idx);
                }
              });
            });
            [...planGoalIds, ...compactGoalIds].forEach((goalId) => {
              const key = `goal_${goalId}`;
              if (!map.has(key)) {
                map.set(key, idx);
              }
            });
          }
        });
        if (typeof latestGoalPlanIndex === 'number') {
          map.set('goalplan_latest', latestGoalPlanIndex);
        }
        if (typeof earliestGoalPlanIndex === 'number') {
          map.set('goalplan_earliest', earliestGoalPlanIndex);
        }
        if (typeof latestAcceptedGoalPlanIndex === 'number') {
          map.set('goalplan_latest_accepted', latestAcceptedGoalPlanIndex);
        }
        if (typeof earliestAcceptedGoalPlanIndex === 'number') {
          map.set('goalplan_earliest_accepted', earliestAcceptedGoalPlanIndex);
        }
        if (typeof latestPendingGoalPlanIndex === 'number') {
          map.set('goalplan_latest_pending', latestPendingGoalPlanIndex);
        }
        if (typeof earliestPendingGoalPlanIndex === 'number') {
          map.set('goalplan_earliest_pending', earliestPendingGoalPlanIndex);
        }
        if (typeof latestDeclinedGoalPlanIndex === 'number') {
          map.set('goalplan_latest_declined', latestDeclinedGoalPlanIndex);
        }
        if (typeof earliestDeclinedGoalPlanIndex === 'number') {
          map.set('goalplan_earliest_declined', earliestDeclinedGoalPlanIndex);
        }
        return map;
      }, [messages]);
      const [externalContext, setExternalContext] = useState(props.context ?? {});
      const readerRef = useRef(null);
      const actionData = useRef([])
      const [agentStatusBadge, setAgentStatusBadge] = useState(null);
      const [planStepActionFeedback, setPlanStepActionFeedback] = useState(null);
      const sendChatRef = useRef(null);
      const lastAutoStepTokenRef = useRef(null);
      const lastTaskAreaRef = useRef(null);
      const translateAdvisorCommandByIndex = useCallback(
        (message) => {
          if (!message || typeof message.content !== 'string') {
            return message;
          }
          const trimmed = message.content.trim();
          if (!trimmed) {
            return message;
          }
          const match = /^(advisor)\s+(accept|decline)\s+(\d+)(\b.*)?$/i.exec(trimmed);
          if (!match) {
            return message;
          }
          const optionNumber = Number(match[3]);
          if (!Number.isFinite(optionNumber)) {
            return message;
          }
          const targetId = advisorSuggestionNumberLookup.numberToId.get(optionNumber);
          if (!targetId) {
            return message;
          }
          const remainder = match[4] ? match[4].trimStart() : '';
          const updatedContent = `${match[1]} ${match[2]} ${targetId}${remainder ? ` ${remainder}` : ''}`;
          if (updatedContent === message.content) {
            return message;
          }
          return { ...message, content: updatedContent };
        },
        [advisorSuggestionNumberLookup]
      );
      const [modeSummary, setModeSummary] = useState({ available: [], active: null });
      const modeRefreshController = useRef(null);
      const [availableChats, setAvailableChats] = useState([]);
      const [activeChatInfo, setActiveChatInfo] = useState(null);
      const [chatLoading, setChatLoading] = useState(false);
      const [chatVersion, bumpChatVersion] = useReducer((count) => count + 1, 0);
      const [categoryVersion, bumpCategoryVersion] = useReducer((count) => count + 1, 0);
      const [planArtifactVersion, bumpPlanArtifactVersion] = useReducer((count) => count + 1, 0);
      const messagesRef = useRef(messages);
      const compactMode = Boolean(props.smallFont || props.compactMode || props.compact);
      const lastPlanPrimitiveIdRef = useRef(null);
      const lastPlanPrimitiveCallbackRef = useRef(null);
      useDataEvent('relationship_update remove_primitives', primitive?.id, () => bumpChatVersion());
      const chatEventIds = useMemo(() => (availableChats ?? []).map((chat) => chat?.id ?? chat?._id).filter(Boolean), [availableChats]);
      useDataEvent('control_update set_field', chatEventIds, () => bumpChatVersion());
      useDataEvent('relationship_update add_primitives remove_primitives', activeChatInfo?.id, () => bumpCategoryVersion());

      const scopePlanContext = agentScope?.planContext ?? null;
      const scopePlanArtifacts = useMemo(() => {
        if (!scopePlanContext?.plan) {
          return null;
        }
        const plan = scopePlanContext.plan;
        return {
          ...plan,
          planPrimitiveId:
            scopePlanContext.planPrimitiveId ??
            plan.planPrimitiveId ??
            plan.id ??
            null,
          focus: plan.focus ?? scopePlanContext.focus ?? null,
          area: plan.area ?? scopePlanContext.area ?? null,
        };
      }, [scopePlanContext]);
      const planPrimitiveId =
        taskState?.planPrimitiveId ??
        activeChatInfo?.planPrimitiveId ??
        planArtifacts?.planPrimitiveId ??
        scopePlanArtifacts?.planPrimitiveId ??
        null;
      const advisorSuggestionPlanIdRef = useRef(null);
      const lastAdvisorChatIdRef = useRef(null);

      const markAdvisorSuggestionsHandled = useCallback(
        (suggestionIds) => {
          const ids = Array.isArray(suggestionIds) ? suggestionIds.filter(Boolean) : [];
          if (!ids.length) {
            return;
          }
          const lookup = new Set(ids);
          setMessages((prev) => {
            let changed = false;
            const next = prev.map((entry) => {
              const entryId = entry?.metadata?.advisorSuggestionId;
              if (!entryId || !lookup.has(entryId)) {
                return entry;
              }
              if (entry.metadata?.advisorSuggestionStatus === 'handled') {
                return entry;
              }
              changed = true;
              const nextMetadata = {
                ...(entry.metadata ?? {}),
                advisorSuggestionStatus: 'handled',
              };
              return {
                ...entry,
                metadata: nextMetadata,
              };
            });
            return changed ? next : prev;
          });
        },
        [setMessages]
      );

      useEffect(() => {
        const nextPlanId = planPrimitiveId ?? null;
        if (advisorSuggestionPlanIdRef.current === null) {
          advisorSuggestionPlanIdRef.current = nextPlanId;
          return;
        }
        if (advisorSuggestionPlanIdRef.current === nextPlanId) {
          return;
        }
        advisorSuggestionPlanIdRef.current = nextPlanId;
        const removedIds = [];
        setAdvisorSuggestions((prev) => {
          if (!prev.length) {
            return prev;
          }
          prev.forEach((entry) => {
            if (entry?.id) {
              removedIds.push(entry.id);
            }
          });
          return [];
        });
        if (removedIds.length) {
          markAdvisorSuggestionsHandled(removedIds);
        }
      }, [planPrimitiveId, markAdvisorSuggestionsHandled]);
      useEffect(() => {
        const nextChatId = activeChatInfo?.id ?? null;
        if (lastAdvisorChatIdRef.current === nextChatId) {
          return;
        }
        lastAdvisorChatIdRef.current = nextChatId;
        advisorSuggestionPlanIdRef.current = null;
        setAdvisorSuggestions([]);
      }, [activeChatInfo?.id, setAdvisorSuggestions]);
      useEffect(() => {
        onAdvisorSuggestionsChange?.(advisorSuggestions);
      }, [advisorSuggestions, onAdvisorSuggestionsChange]);
      const upsertAdvisorBadgeMessage = useCallback(
        (suggestion) => {
          if (!suggestion?.id) {
            return;
          }
          const badgeData = buildAdvisorBadgeData(suggestion);
          if (!badgeData) {
            return;
          }
          const serializedPayload = serializeAdvisorSuggestionPayload(suggestion);
          const baseMetadata = {
            advisorSuggestionId: suggestion.id,
            advisorSuggestionStatus: 'active',
          };
          if (suggestion.type) {
            baseMetadata.advisorSuggestionType = suggestion.type;
          }
          if (serializedPayload) {
            baseMetadata.advisorSuggestionPayload = serializedPayload;
          }
          setMessages((prev) => {
            const idx = prev.findIndex(
              (entry) => entry?.metadata?.advisorSuggestionId === suggestion.id
            );
            if (idx === -1) {
              return [
                ...prev,
                {
                  role: 'assistant',
                  content: '',
                  badgeType: 'advisor',
                  badgeData,
                  metadata: baseMetadata,
                },
              ];
            }
            const current = prev[idx];
            const shouldUpdate =
              !current.badgeData ||
              current.badgeData.message !== badgeData.message ||
              current.badgeData.note !== badgeData.note ||
              current.badgeData.typeLabel !== badgeData.typeLabel ||
              current.badgeData.actionable !== badgeData.actionable;
            if (!shouldUpdate) {
              return prev;
            }
            const next = [...prev];
            const nextMetadata = {
              ...(current.metadata ?? {}),
              ...baseMetadata,
            };
            next[idx] = { ...current, badgeData, metadata: nextMetadata };
            return next;
          });
        },
        [setMessages]
      );

      const planFromState = useMemo(() => {
        if (!taskState?.plan?.steps?.length) {
          return null;
        }
        const normalizedSteps = taskState.plan.steps.map((step, idx) => {
          const order = Number.isFinite(step.order) ? step.order : idx;
          const metadata = mergePlanStepMetadata(step.metadata, null);
          return {
            ...step,
            runtime: step.runtime ?? null,
            metadata: metadata ?? null,
            order,
            primitiveId: step.primitiveId ?? step.id ?? `${taskState.plan.recipeId || 'plan'}_${idx}`,
          };
        });
        return {
          source: 'session',
          planPrimitiveId: taskState.planPrimitiveId ?? planArtifacts?.planPrimitiveId ?? null,
          title: taskState.plan.title,
          summary: taskState.plan.summary,
          recipeId: taskState.plan.recipeId ?? planArtifacts?.recipeId ?? null,
          focus: taskState.focus ?? planArtifacts?.focus ?? null,
          area: taskState.area ?? planArtifacts?.area ?? null,
          status: taskState.plan.status ?? planArtifacts?.status ?? null,
          goals: Array.isArray(taskState.planReference?.goals)
            ? taskState.planReference.goals
            : planArtifacts?.goals ?? [],
          pendingGoalPlan:
            taskState?.planReference?.pendingGoalPlan ??
            planArtifacts?.pendingGoalPlan ??
            null,
          archivedGoalIds: Array.isArray(taskState.planReference?.archivedGoalIds)
            ? taskState.planReference.archivedGoalIds
            : planArtifacts?.archivedGoalIds ?? [],
          activeGoalId: taskState.planReference?.activeGoalId ?? planArtifacts?.activeGoalId ?? null,
          activeStepKey:
            taskState.plan?.runtime?.activeStepKey ??
            taskState.planReference?.activeStepKey ??
            planArtifacts?.activeStepKey ??
            null,
          runtimeVersion:
            taskState.plan?.runtime?.version ??
            taskState.planReference?.runtimeVersion ??
            planArtifacts?.runtimeVersion ??
            null,
          steps: normalizedSteps,
          activeStepId:
            normalizedSteps.find((step) => (step?.runtime?.status ?? '') === 'in_progress')?.primitiveId ??
            planArtifacts?.activeStepId ??
            null,
        };
      }, [taskState, planArtifacts]);

      const preferScopedPlan = Boolean(scopePlanArtifacts && scopePlanContext?.workstreamId);
      const planDisplay = useMemo(() => {
        const expectedPlanId = planPrimitiveId ? String(planPrimitiveId) : null;
        const matchesExpectedPlanId = (candidatePlanId) => {
          if (!expectedPlanId) {
            return true;
          }
          if (!candidatePlanId) {
            return false;
          }
          return String(candidatePlanId) === expectedPlanId;
        };
        const filteredPlanFromState =
          planFromState && matchesExpectedPlanId(planFromState.planPrimitiveId)
            ? planFromState
            : null;
        const preferredArtifacts = preferScopedPlan ? scopePlanArtifacts : (planArtifacts ?? scopePlanArtifacts);
        const artifacts =
          preferredArtifacts && matchesExpectedPlanId(preferredArtifacts.planPrimitiveId)
            ? preferredArtifacts
            : null;
        if (!artifacts && !filteredPlanFromState) {
          return null;
        }
        if (!artifacts) {
          return planPrimitiveId
            ? { ...filteredPlanFromState, planPrimitiveId }
            : filteredPlanFromState;
        }

        const artifactPlan = {
          ...artifacts,
          planPrimitiveId: planPrimitiveId ?? artifacts.planPrimitiveId ?? null,
        };

        const statePlanId = filteredPlanFromState?.planPrimitiveId ?? null;
        const artifactPlanId = artifactPlan?.planPrimitiveId ?? null;
        const stateRecipeId = filteredPlanFromState?.recipeId ?? null;
        const artifactRecipeId = artifactPlan?.recipeId ?? null;
        const planIdMismatch =
          Boolean(statePlanId && artifactPlanId && String(statePlanId) !== String(artifactPlanId));
        const recipeMismatch =
          Boolean(stateRecipeId && artifactRecipeId && stateRecipeId !== artifactRecipeId);
        if (planIdMismatch || recipeMismatch) {
          return artifactPlan;
        }

        if (!filteredPlanFromState?.steps?.length) {
          return artifactPlan;
        }

        const sessionByKey = new Map();
        for (const step of filteredPlanFromState.steps) {
          if (!step) continue;
          const keys = [step.id, step.primitiveId].filter(Boolean);
          keys.forEach((key) => sessionByKey.set(key, step));
        }

        const enrichedSteps = (artifactPlan.steps ?? []).map((step, idx) => {
          const key = step?.id ?? step?.primitiveId;
          const sessionStep = (key && sessionByKey.get(key)) || null;
          const mergedRuntime = {
            ...(sessionStep?.runtime ?? {}),
            ...(step?.runtime ?? {}),
          };
          if (!sessionStep) {
            return {
              ...step,
              runtime: mergedRuntime,
              metadata: normalizePlanStepMetadata(step.metadata) ?? null,
              order: Number.isFinite(step?.order) ? step.order : idx,
            };
          }
          return {
            ...step,
            runtime: mergedRuntime,
            metadata: mergePlanStepMetadata(sessionStep.metadata, step.metadata),
            order: Number.isFinite(step?.order) ? step.order : Number.isFinite(sessionStep?.order) ? sessionStep.order : idx,
            selection: sessionStep.selection ?? step.selection ?? null,
            selectedActionId: sessionStep.selectedActionId ?? step.selectedActionId ?? null,
            executionState: mergeExecutionState(sessionStep.executionState, step.executionState),
          };
        });

        const existingKeys = new Set();
        enrichedSteps.forEach((step) => {
          if (!step) {
            return;
          }
          const keys = [step.primitiveId, step.id].filter(Boolean);
          keys.forEach((key) => existingKeys.add(key));
        });

        // When artifact steps are available, treat them as canonical and only
        // overlay matching session execution state. Do not append session-only
        // steps here, as stale session state can leak steps from other recipes.
        const mergedSteps = [...enrichedSteps]
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((step, idx) => ({ ...step, order: idx }));

        const mergedPlan = {
          ...artifactPlan,
          ...filteredPlanFromState,
          steps: mergedSteps,
          activeStepKey:
            artifactPlan.activeStepKey ??
            filteredPlanFromState.activeStepKey ??
            null,
          runtimeVersion:
            artifactPlan.runtimeVersion ??
            filteredPlanFromState.runtimeVersion ??
            null,
        };

        if (!mergedPlan.activeStepId) {
          const sessionActiveStepId =
            filteredPlanFromState.activeStepId ??
            mergedSteps.find((step) => (step?.runtime?.status ?? '') === 'in_progress')?.primitiveId ??
            null;
          mergedPlan.activeStepId = sessionActiveStepId ?? artifactPlan.activeStepId ?? null;
        }
        if (!mergedPlan.activeStepKey && mergedPlan.activeStepId) {
          const activeStep = mergedSteps.find((step) => {
            const candidates = [step?.primitiveId, step?.id].filter(Boolean).map((value) => String(value));
            return candidates.includes(String(mergedPlan.activeStepId));
          });
          mergedPlan.activeStepKey = activeStep?.runtime?.key ?? activeStep?.primitiveId ?? activeStep?.id ?? null;
        }
        if (!mergedPlan.activeStepId && mergedPlan.activeStepKey) {
          const activeStep = mergedSteps.find((step) => {
            const key = step?.runtime?.key ?? step?.primitiveId ?? step?.id ?? null;
            return key && String(key) === String(mergedPlan.activeStepKey);
          });
          mergedPlan.activeStepId = activeStep?.primitiveId ?? activeStep?.id ?? null;
        }

        return mergedPlan;
      }, [planFromState, planArtifacts, scopePlanArtifacts, planPrimitiveId, preferScopedPlan]);

      const pendingGoalPlanForCard = useMemo(
        () =>
          taskState?.planReference?.pendingGoalPlan ??
          planDisplay?.pendingGoalPlan ??
          null,
        [taskState?.planReference?.pendingGoalPlan, planDisplay?.pendingGoalPlan]
      );
      const pendingGoalPlanSyncKey = useMemo(() => {
        const pending = pendingGoalPlanForCard;
        if (!pending?.id) {
          return '';
        }
        const goalCount = Array.isArray(pending.goals) ? pending.goals.length : 0;
        const stepPreviewCount = Array.isArray(pending.goalStepPreview)
          ? pending.goalStepPreview.length
          : 0;
        const gapFillCount = Array.isArray(pending.gapFillPreview)
          ? pending.gapFillPreview.length
          : 0;
        const sourcePreviewCount = Array.isArray(pending.dataSourcePreview)
          ? pending.dataSourcePreview.length
          : 0;
        const stamp = pending.updatedAt ?? pending.createdAt ?? '';
        return `${pending.id}|${stamp}|${goalCount}|${stepPreviewCount}|${gapFillCount}|${sourcePreviewCount}`;
      }, [pendingGoalPlanForCard]);

      const planPrimitive = useMemo(
        () => (planPrimitiveId ? mainstore.primitive(planPrimitiveId) : null),
        [planPrimitiveId, planArtifactVersion, mainstore],
      );

      useEffect(() => {
        if (!planDisplay?.steps?.length) {
          return;
        }
        const suggestionIds = new Set(
          planDisplay.steps
            .map((step) => step?.metadata?.suggestionId)
            .filter(Boolean)
        );
        if (!suggestionIds.size) {
          return;
        }
        const removedIds = [];
        setAdvisorSuggestions((prev) => {
          if (!prev.length) {
            return prev;
          }
          const next = prev.filter((suggestion) => {
            const shouldRemove = suggestionIds.has(suggestion.id);
            if (shouldRemove && suggestion?.id) {
              removedIds.push(suggestion.id);
            }
            return !shouldRemove;
          });
          return next.length === prev.length ? prev : next;
        });
        if (removedIds.length) {
          markAdvisorSuggestionsHandled(removedIds);
        }
      }, [planDisplay?.steps, markAdvisorSuggestionsHandled]);

      const archivedGoalIdSet = useMemo(() => {
        const ids = Array.isArray(planDisplay?.archivedGoalIds)
          ? planDisplay.archivedGoalIds
          : [];
        return new Set(ids.filter(Boolean).map((id) => String(id)));
      }, [planDisplay?.archivedGoalIds]);
      const planSteps = useMemo(() => {
        const steps = planDisplay?.steps ?? [];
        if (!archivedGoalIdSet.size) {
          return steps;
        }
        return steps.filter((step) => {
          const goalId = step?.metadata?.goalId ?? null;
          if (!goalId) {
            return true;
          }
          return !archivedGoalIdSet.has(String(goalId));
        });
      }, [planDisplay?.steps, archivedGoalIdSet]);
      const normalizeStepKey = useCallback((value) => {
        if (value === null || value === undefined) {
          return null;
        }
        return String(value);
      }, []);
      const activePlanStepId = useMemo(() => {
        if (!planSteps.length) {
          return null;
        }
        const activeKey = planDisplay?.activeStepKey ?? null;
        if (activeKey) {
          const matchedByKey = planSteps.find((step) => {
            const key = step?.runtime?.key ?? step?.primitiveId ?? step?.id ?? null;
            return key && String(key) === String(activeKey);
          });
          if (matchedByKey) {
            return matchedByKey?.primitiveId ?? matchedByKey?.id ?? null;
          }
        }
        const inProgressSteps = planSteps.filter(
          (step) => String(step?.runtime?.status ?? '').toLowerCase() === 'in_progress'
        );
        if (inProgressSteps.length === 1) {
          return inProgressSteps[0]?.primitiveId ?? inProgressSteps[0]?.id ?? null;
        }
        if (inProgressSteps.length > 1) {
          const ordered = [...inProgressSteps].sort(
            (a, b) => (Number.isFinite(a?.order) ? a.order : 0) - (Number.isFinite(b?.order) ? b.order : 0)
          );
          const first = ordered[0];
          return first?.primitiveId ?? first?.id ?? null;
        }

        const active = planDisplay?.activeStepId ?? null;
        const normalizedActive = normalizeStepKey(active);
        if (!normalizedActive) {
          return null;
        }
        const exists = planSteps.some((step) => {
          const stepId = normalizeStepKey(step?.id);
          const primitiveId = normalizeStepKey(step?.primitiveId);
          return stepId === normalizedActive || primitiveId === normalizedActive;
        });
        return exists ? active : null;
      }, [planDisplay?.activeStepId, planDisplay?.activeStepKey, planSteps, normalizeStepKey]);
      const onPlanStepFocus = typeof props.onPlanStepFocus === 'function' ? props.onPlanStepFocus : null;
      const focusedPlanStepId = normalizeStepKey(props.focusedPlanStepId ?? null);
      const focusedGoalId = useMemo(() => {
        if (!focusedPlanStepId || !planSteps.length) {
          return null;
        }
        const matched = planSteps.find((step) => {
          const stepId = normalizeStepKey(step?.id ?? step?.stepId ?? null);
          const primitiveId = normalizeStepKey(step?.primitiveId ?? null);
          return stepId === focusedPlanStepId || primitiveId === focusedPlanStepId;
        });
        return matched?.metadata?.goalId ?? null;
      }, [focusedPlanStepId, planSteps, normalizeStepKey]);
      const planStepIdsByGoalId = useMemo(() => {
        const map = new Map();
        planSteps.forEach((step) => {
          const goalId = step?.metadata?.goalId ?? null;
          if (!goalId) {
            return;
          }
          if (!map.has(goalId)) {
            map.set(goalId, []);
          }
          map.get(goalId).push(step);
        });
        return map;
      }, [planSteps]);
      const focusGoalPlanArtefacts = useCallback(
        (goalIds) => {
          if (!onPlanStepFocus) {
            return;
          }
          const normalizedGoalIds = (Array.isArray(goalIds) ? goalIds : [])
            .map((goalId) => (goalId === null || goalId === undefined ? null : String(goalId)))
            .filter(Boolean);
          if (!normalizedGoalIds.length) {
            onPlanStepFocus(null);
            return;
          }
          if (focusedGoalId && normalizedGoalIds.includes(String(focusedGoalId))) {
            onPlanStepFocus(null);
            return;
          }
          const goalSteps = normalizedGoalIds.flatMap((goalId) => planStepIdsByGoalId.get(goalId) ?? []);
          if (!goalSteps.length) {
            onPlanStepFocus(null);
            return;
          }
          const ordered = [...goalSteps].sort((a, b) => {
            const aOrder = Number.isFinite(a?.order) ? a.order : Number.MAX_SAFE_INTEGER;
            const bOrder = Number.isFinite(b?.order) ? b.order : Number.MAX_SAFE_INTEGER;
            return aOrder - bOrder;
          });
          const target = ordered[0];
          const targetId = target?.id ?? target?.stepId ?? target?.primitiveId ?? null;
          const stepIds = ordered
            .map((step) => step?.id ?? step?.stepId ?? step?.primitiveId ?? null)
            .filter(Boolean);
          onPlanStepFocus({ anchorStepId: targetId ?? null, stepIds });
        },
        [focusedGoalId, onPlanStepFocus, planStepIdsByGoalId],
      );
      const planStepLeafIds = useMemo(() => {
        if (!planSteps.length) {
          return new Set();
        }
        const referenced = new Set();
        const stepIds = new Set();
        planSteps.forEach((step) => {
          const stepId = resolvePlanStepId(step);
          if (stepId) {
            stepIds.add(stepId);
          }
          const inputs = Array.isArray(step?.inputs) ? step.inputs : [];
          inputs.forEach((input) => {
            if (input?.stepId) {
              referenced.add(input.stepId);
            }
          });
        });
        const leafIds = new Set();
        stepIds.forEach((id) => {
          if (!referenced.has(id)) {
            leafIds.add(id);
          }
        });
        return leafIds;
      }, [planSteps]);
      const subplanGroups = useMemo(() => {
        if (!planSteps.length) {
          return [];
        }
        const stepLookup = new Map();
        planSteps.forEach((step) => {
          if (!step) {
            return;
          }
          const keys = [resolvePlanStepId(step), step?.id, step?.stepId, step?.primitiveId]
            .filter(Boolean)
            .map((value) => String(value));
          const runtimeKey = step?.runtime?.key ? String(step.runtime.key) : null;
          const selectedActionId = step?.selectedActionId ? String(step.selectedActionId) : null;
          if (runtimeKey) {
            keys.push(runtimeKey);
          }
          if (selectedActionId) {
            keys.push(selectedActionId);
          }
          keys.forEach((key) => stepLookup.set(key, step));
        });
        const getRequestedByStepId = (step) => {
          if (!step) {
            return null;
          }
          const pipelineMeta =
            step?.metadata?.pipeline ??
            step?.executionState?.pipeline ??
            null;
          const metadataRequestedBy =
            step?.metadata?.requestedByStepId ??
            step?.metadata?.requested_by_step_id ??
            null;
          const executionRequestedBy =
            step?.executionState?.requestedByStepId ??
            step?.executionState?.requested_by_step_id ??
            null;
          const pipelineRequestedBy =
            pipelineMeta?.requestedByStepId ??
            pipelineMeta?.requested_by_step_id ??
            null;
          const runnerContextRequestedBy =
            step?.executionState?.runner?.context?.requestedByStepId ??
            step?.executionState?.runner?.context?.requested_by_step_id ??
            null;
          const frameworkRequestedBy =
            step?.executionState?.runner?.context?.task_framework_context?.requestedByStepId ??
            step?.executionState?.runner?.context?.task_framework_context?.requested_by_step_id ??
            null;
          return (
            metadataRequestedBy ??
            executionRequestedBy ??
            pipelineRequestedBy ??
            runnerContextRequestedBy ??
            frameworkRequestedBy ??
            null
          );
        };
        const goalKeyCache = new Map();
        const resolveInheritedGoalKey = (step, path = new Set()) => {
          if (!step) {
            return null;
          }
          const stepKey = String(resolvePlanStepId(step) ?? step?.id ?? '');
          if (!stepKey) {
            return null;
          }
          if (goalKeyCache.has(stepKey)) {
            return goalKeyCache.get(stepKey);
          }
          if (path.has(stepKey)) {
            goalKeyCache.set(stepKey, null);
            return null;
          }
          path.add(stepKey);
          const directQueueGroupId = step?.metadata?.queueGroupId ?? null;
          const directGoalPlanId = step?.metadata?.goalPlanId ?? null;
          if (directQueueGroupId) {
            const queueGroupRaw = String(directQueueGroupId ?? '').trim();
            const directQueueKey = queueGroupRaw.includes('::')
              ? `goalq_${queueGroupRaw}`
              : (directGoalPlanId
                ? `goalq_${directGoalPlanId}::${queueGroupRaw}`
                : `goalq_${queueGroupRaw}`);
            goalKeyCache.set(stepKey, directQueueKey);
            return directQueueKey;
          }
          const directGoalId = step?.metadata?.goalId ?? null;
          if (directGoalId) {
            const directKey = directGoalPlanId
              ? `goal_${directGoalPlanId}::${directGoalId}`
              : `goal_${directGoalId}`;
            goalKeyCache.set(stepKey, directKey);
            return directKey;
          }
          const requestedById = getRequestedByStepId(step);
          const parentStep = requestedById ? stepLookup.get(String(requestedById)) : null;
          const inherited = parentStep ? resolveInheritedGoalKey(parentStep, path) : null;
          goalKeyCache.set(stepKey, inherited ?? null);
          return inherited ?? null;
        };
        const groups = new Map();
        planSteps.forEach((step) => {
          if (!step) {
            return;
          }
          const origin = resolveStepOrigin(step);
          let key = resolveSubplanKey(step);
          if (
            (origin === 'advisor' || origin === 'gap_fill') &&
            !String(key).startsWith('goal_') &&
            !String(key).startsWith('goalq_')
          ) {
            const inheritedGoalKey = resolveInheritedGoalKey(step);
            if (inheritedGoalKey) {
              key = inheritedGoalKey;
            }
          }
          if (!groups.has(key)) {
            const normalizedKey = String(key);
            const groupOrigin =
              normalizedKey.startsWith('goal_') || normalizedKey.startsWith('goalq_')
                ? 'goal'
                : origin;
            groups.set(key, {
              id: key,
              steps: [],
              origin: groupOrigin,
              label: null,
              insertAfterIndex: -1,
            });
          }
          groups.get(key).steps.push(step);
        });
        const goalGroups = Array.from(groups.values()).filter((group) => {
          const gid = String(group?.id ?? '');
          return gid.startsWith('goal_') || gid.startsWith('goalq_');
        });
        groups.forEach((group) => {
          const seenStepIds = new Set();
          const dedupedSteps = [];
          (group.steps || []).forEach((step) => {
            const stepIdentity =
              resolvePlanStepId(step) ??
              step?.primitiveId ??
              step?.id ??
              null;
            const normalizedIdentity = stepIdentity ? String(stepIdentity) : null;
            if (normalizedIdentity && seenStepIds.has(normalizedIdentity)) {
              return;
            }
            if (normalizedIdentity) {
              seenStepIds.add(normalizedIdentity);
            }
            dedupedSteps.push(step);
          });
          group.steps = dedupedSteps;
        });
        groups.forEach((group) => {
          const firstStep = group.steps[0];
          const origin = group.origin;
          let labelMeta = {};
          if (origin === 'goal') {
            const derivedGoalPlanId = firstStep?.metadata?.goalPlanId ?? null;
            const derivedQueueGroupId = firstStep?.metadata?.queueGroupId ?? null;
            const groupIdRaw = String(group?.id ?? '');
            const derivedGoalIdFromGroup = groupIdRaw.startsWith('goal_')
              ? (() => {
                  const token = groupIdRaw.slice(5);
                  const parts = token.split('::');
                  return parts[parts.length - 1] || null;
                })()
              : null;
            const derivedGoalId =
              firstStep?.metadata?.goalId ??
              derivedGoalIdFromGroup;
            const goalMeta =
              (
                derivedQueueGroupId && derivedGoalPlanId
                  ? goalPlanMetaByQueueGroupId.get(
                      String(derivedQueueGroupId).includes('::')
                        ? `goalq_${derivedQueueGroupId}`
                        : `goalq_${derivedGoalPlanId}::${derivedQueueGroupId}`,
                    )
                  : null
              ) ??
              (derivedQueueGroupId
                ? (
                    goalPlanMetaByQueueGroupId.get(`goalq_${String(derivedQueueGroupId)}`) ??
                    goalPlanMetaByQueueGroupId.get(String(derivedQueueGroupId))
                  )
                : null) ??
              goalPlanMetaByGoalId.get(derivedGoalId) ??
              {};
            labelMeta.goalLabel = goalMeta.label ?? null;
            labelMeta.goalPlanLabel = goalMeta.planLabel ?? null;
            group.goalPlanId = goalMeta.goalPlanId ?? null;
            if (goalMeta.goalPlanId) {
              const planIdx = subplanInsertIndexMap.get(`goalplan_${goalMeta.goalPlanId}`);
              if (typeof planIdx === 'number') {
                group.insertAfterIndex = planIdx;
              }
            } else if (typeof goalMeta.sourceIndex === 'number') {
              // Back-compat: old chat history may not have a goalPlanId on the badge entry.
              // Keep the block anchored near the originating goal-plan card by message index.
              group.insertAfterIndex = goalMeta.sourceIndex;
            } else {
              const doneStates = new Set(['done', 'completed', 'success', 'skipped']);
              const isCompletedGoalGroup = group.steps.every((step) =>
                doneStates.has(String(step?.runtime?.status ?? '').toLowerCase()),
              );
              if (isCompletedGoalGroup) {
                const earliestAcceptedGoalPlanIndex = subplanInsertIndexMap.get('goalplan_earliest_accepted');
                if (typeof earliestAcceptedGoalPlanIndex === 'number') {
                  group.insertAfterIndex = earliestAcceptedGoalPlanIndex;
                } else {
                  const earliestGoalPlanIndex = subplanInsertIndexMap.get('goalplan_earliest');
                  if (typeof earliestGoalPlanIndex === 'number') {
                    group.insertAfterIndex = earliestGoalPlanIndex;
                  }
                }
              } else {
                const latestPendingGoalPlanIndex = subplanInsertIndexMap.get('goalplan_latest_pending');
                if (typeof latestPendingGoalPlanIndex === 'number') {
                  group.insertAfterIndex = latestPendingGoalPlanIndex;
                } else {
                  const latestGoalPlanIndex = subplanInsertIndexMap.get('goalplan_latest');
                  if (typeof latestGoalPlanIndex === 'number') {
                    group.insertAfterIndex = latestGoalPlanIndex;
                  }
                }
              }
            }
          }
          if (origin === 'advisor' || origin === 'gap_fill') {
            const suggestionId = firstStep?.metadata?.suggestionId;
            if (suggestionId && advisorLabelById.has(suggestionId)) {
              labelMeta.advisorLabel = advisorLabelById.get(suggestionId);
            }
          }
          const resolvedLabel = resolveSubplanLabel(firstStep, {
            advisorLabel: labelMeta.advisorLabel,
            goalLabel: labelMeta.goalLabel,
          });
          group.label = resolvedLabel;
          const groupKey = group.id;
          const fallbackIndex = group.insertAfterIndex ?? -1;
          group.insertAfterIndex = resolveSubplanInsertionIndex(groupKey, subplanInsertIndexMap, fallbackIndex);
          if (origin === 'core') {
            group.insertAfterIndex = -1;
          }
        });
        return Array.from(groups.values()).map((group) => {
          const stepStates = group.steps.map((step) => derivePlanStepStatus(step, mainstore, activePlanStepId));
          const isComplete = stepStates.every((state) => ['done', 'skipped'].includes(state.state));
          const hasRunning = stepStates.some((state) => state.state === 'running');
          const hasQueued = stepStates.some((state) => state.state === 'queued');
          const resolved = {
            ...group,
            stepStates,
            isComplete,
            hasRunning,
            hasQueued,
          };
          if (preferScopedPlan && resolved.origin !== 'core' && resolved.insertAfterIndex < 0) {
            return null;
          }
          return resolved;
        }).filter(Boolean);
      }, [planSteps, advisorLabelById, goalPlanMetaByGoalId, goalPlanMetaByQueueGroupId, subplanInsertIndexMap, mainstore, preferScopedPlan, activePlanStepId]);
      const awaitingInputDetails = useMemo(() => {
        if (!planSteps.length) {
          return [];
        }
        const details = [];
        planSteps.forEach((step) => {
          const state = derivePlanStepStatus(step, mainstore, activePlanStepId);
          const entry = buildAwaitingInputDetails(step, state);
          if (entry) {
            details.push(entry);
          }
        });
        return details;
      }, [planSteps, activePlanStepId]);

      useEffect(() => {
        if (!awaitingInputDetails.length) {
          return;
        }
        // Clear sticky [[update:...]] banners once the plan has transitioned into an explicit input-needed state.
        statusPinnedRef.current = false;
        setAgentStatusBadge(null);
      }, [awaitingInputDetails]);
      useEffect(() => {
        if (!planStepActionFeedback) {
          return;
        }
        const timeoutId = window.setTimeout(() => {
          setPlanStepActionFeedback(null);
        }, 3500);
        return () => window.clearTimeout(timeoutId);
      }, [planStepActionFeedback]);
      const planBlocksKey = useMemo(() => {
        if (!subplanGroups.length) {
          return '';
        }
        return subplanGroups
          .map(
            (group) =>
              `${group.id}:${group.steps.length}:${group.isComplete ? 1 : 0}:${group.hasRunning ? 1 : 0}:${group.hasQueued ? 1 : 0}`,
          )
          .join('|');
      }, [subplanGroups]);
      const planBlocksRenderedRef = useRef(planBlocksKey);
      const toggleSubplanCollapsed = useCallback((subplanId) => {
        if (!subplanId) {
          return;
        }
        setCollapsedSubplanState((prev) => {
          const current = prev?.[subplanId];
          return {
            ...prev,
            [subplanId]: !current,
          };
        });
      }, []);
      const handlePlanStepActivate = useCallback(async (step, mode = null) => {
        if (!planPrimitiveId || !step) {
          return;
        }
        const stepId = resolvePlanStepId(step);
        if (!stepId) {
          return;
        }
        const runtimeStatus = String(step?.runtime?.status ?? '').toLowerCase();
        const isTerminalDone = ['done', 'completed', 'success'].includes(runtimeStatus);
        const effectiveMode = mode ?? (isTerminalDone ? 'retry' : null);
        try {
          const response = await fetch(`/api/plan/${planPrimitiveId}/activate_step`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ stepId, mode: effectiveMode }),
          });
          const payload = await response.json().catch(() => null);
          if (!response.ok || !payload?.success) {
            throw new Error(payload?.error || 'Failed to update plan step');
          }
          const result = payload?.result ?? {};
          const action = result?.action ?? null;
          const reason = result?.reason ?? null;
          const mutated = result?.mutated !== false;
          if (typeof mainstore.fetchPrimitive === 'function') {
            await mainstore.fetchPrimitive(planPrimitiveId, { force: true });
          }
          if (mutated) {
            setPlanStepActionFeedback({
              stepId,
              stepPrimitiveId: step?.primitiveId ?? null,
              mode: action === 'retry' || action === 'skip' ? action : effectiveMode,
              at: Date.now(),
            });
          }
          const statusLabel =
            action === 'skip'
              ? 'Skipped step'
              : action === 'retry'
              ? 'Retry queued'
              : action === 'noop' && reason === 'already_terminal'
              ? 'Already completed'
              : 'Set current step';
          setAgentStatus(`update:${statusLabel}: ${step?.name ?? 'Step'}`);
          bumpPlanArtifactVersion();
          if (effectiveMode === 'retry' && sendChatRef.current) {
            // Retry should immediately kick execution, equivalent to user typing "continue".
            await sendChatRef.current({
              autoTrigger: true,
              autoMessage: { role: 'user', content: 'continue' },
            });
          }
        } catch (error) {
          console.warn('Failed to update plan step', error);
        }
      }, [planPrimitiveId, mainstore, bumpPlanArtifactVersion]);
      const handlePlanStepDelete = useCallback(async (step) => {
        if (!planPrimitiveId || !step) {
          return;
        }
        const stepId = resolvePlanStepId(step);
        if (!stepId) {
          return;
        }
        try {
          const response = await fetch(
            `/api/plan/${planPrimitiveId}/steps/${encodeURIComponent(stepId)}`,
            { method: 'DELETE' }
          );
          const payload = await response.json().catch(() => null);
          if (!response.ok || !payload?.success) {
            throw new Error(payload?.error || 'Failed to delete plan step');
          }
          if (typeof mainstore.fetchPrimitive === 'function') {
            await mainstore.fetchPrimitive(planPrimitiveId, { force: true });
          }
          bumpPlanArtifactVersion();
        } catch (error) {
          console.warn('Failed to delete plan step', error);
        }
      }, [planPrimitiveId, mainstore, bumpPlanArtifactVersion]);
      const renderMessages = useMemo(() => {
        const stripKonvaPreviews = (entries) =>
          (Array.isArray(entries) ? entries : []).filter((entry) => !entry?.konvaPreview);
        if (!subplanGroups.length) {
          if (!archivedGoalIdSet.size) {
            return stripKonvaPreviews(messages);
          }
          return stripKonvaPreviews(messages).filter((entry) => {
            if (!entry || entry.badgeType !== 'goal_plan') {
              return true;
            }
            const planGoals = Array.isArray(entry?.badgeData?.plan?.goals)
              ? entry.badgeData.plan.goals
              : [];
            if (!planGoals.length) {
              return true;
            }
            return !planGoals.every((goal) => archivedGoalIdSet.has(String(goal?.id ?? '')));
          });
        }
        const acceptedGoalPlanIds = new Set();
        subplanGroups.forEach((group) => {
          if (group?.origin !== 'goal') {
            return;
          }
          if (group?.goalPlanId) {
            acceptedGoalPlanIds.add(group.goalPlanId);
          }
        });
        const shouldKeepGoalPlanEntry = (entry) => {
          if (!entry || entry.badgeType !== 'goal_plan') {
            return true;
          }
          const planGoals = Array.isArray(entry?.badgeData?.plan?.goals)
            ? entry.badgeData.plan.goals
            : [];
          if (
            archivedGoalIdSet.size &&
            planGoals.length &&
            planGoals.every((goal) => archivedGoalIdSet.has(String(goal?.id ?? '')))
          ) {
            return false;
          }
          if (!acceptedGoalPlanIds.size) {
            return true;
          }
          const status = entry?.badgeData?.status ?? null;
          if (status !== 'accepted') {
            return true;
          }
          const goalPlanId = resolveGoalPlanIdFromEntry(entry);
          return !goalPlanId || !acceptedGoalPlanIds.has(goalPlanId);
        };
        const filteredMessages = [];
        const retainedOriginalIndices = [];
        messages.forEach((entry, originalIdx) => {
          if (!shouldKeepGoalPlanEntry(entry)) {
            return;
          }
          retainedOriginalIndices.push(originalIdx);
          filteredMessages.push(entry);
        });
        const insertions = new Map();
        const projectOriginalAfterIndexToFiltered = (originalAfterIdx) => {
          if (!Number.isFinite(originalAfterIdx)) {
            return -1;
          }
          let count = 0;
          for (let i = 0; i < retainedOriginalIndices.length; i += 1) {
            if (retainedOriginalIndices[i] <= originalAfterIdx) {
              count += 1;
              continue;
            }
            break;
          }
          return count - 1;
        };
        const findLastGoalPlanOriginalIndex = ({ goalPlanId = null, status = null } = {}) => {
          for (let idx = messages.length - 1; idx >= 0; idx -= 1) {
            const entry = messages[idx];
            if (!entry || entry.badgeType !== 'goal_plan') {
              continue;
            }
            if (goalPlanId && resolveGoalPlanIdFromEntry(entry) !== goalPlanId) {
              continue;
            }
            const entryStatus = entry?.badgeData?.status ?? entry?.metadata?.goalPlanStatus ?? null;
            if (status && entryStatus !== status) {
              continue;
            }
            return idx;
          }
          return -1;
        };
        const autoStepAnchorByStepId = new Map();
        const autoStepPattern = /\[\[auto_step:([^\]]+)\]\]/g;
        messages.forEach((entry, idx) => {
          const metadataStepId = entry?.metadata?.autoStep?.stepId;
          if (metadataStepId) {
            autoStepAnchorByStepId.set(String(metadataStepId), idx);
          }
          const content = typeof entry?.content === 'string' ? entry.content : '';
          if (!content.includes('[[auto_step:')) {
            return;
          }
          let match = autoStepPattern.exec(content);
          while (match) {
            const stepId = typeof match?.[1] === 'string' ? match[1].trim() : '';
            if (stepId) {
              autoStepAnchorByStepId.set(stepId, idx);
            }
            match = autoStepPattern.exec(content);
          }
          autoStepPattern.lastIndex = 0;
        });
        subplanGroups.forEach((group) => {
          let hasExplicitAutoStepAnchor = false;
          let insertAfterIndex = Number.isFinite(group.insertAfterIndex)
            ? projectOriginalAfterIndexToFiltered(group.insertAfterIndex)
            : -1;
          const groupStepIds = new Set(
            (Array.isArray(group?.steps) ? group.steps : [])
              .map((step) => resolvePlanStepId(step) ?? step?.id ?? null)
              .filter(Boolean)
              .map((id) => String(id)),
          );
          let latestAutoStepAnchorOriginal = -1;
          groupStepIds.forEach((stepId) => {
            const candidate = autoStepAnchorByStepId.get(stepId);
            if (Number.isFinite(candidate) && candidate > latestAutoStepAnchorOriginal) {
              latestAutoStepAnchorOriginal = candidate;
            }
          });
          const autoStepAnchorIdx = projectOriginalAfterIndexToFiltered(latestAutoStepAnchorOriginal);
          if (group?.origin !== 'core' && autoStepAnchorIdx >= 0) {
            // Prefer explicit auto-step anchors over plan-level fallback anchors.
            // This keeps each completed block near the message that reported it.
            insertAfterIndex = autoStepAnchorIdx;
            hasExplicitAutoStepAnchor = true;
          }
          if (!hasExplicitAutoStepAnchor && group?.origin === 'goal') {
            if (group?.goalPlanId && insertAfterIndex < 0) {
              const explicitOriginalIdx = findLastGoalPlanOriginalIndex({ goalPlanId: group.goalPlanId });
              const explicitIdx = projectOriginalAfterIndexToFiltered(explicitOriginalIdx);
              if (explicitIdx >= 0) {
                insertAfterIndex = explicitIdx;
              }
            } else if (insertAfterIndex < 0) {
              const preferredStatus = group?.isComplete ? 'accepted' : 'pending';
              const statusIdx = projectOriginalAfterIndexToFiltered(
                findLastGoalPlanOriginalIndex({ status: preferredStatus }),
              );
              if (statusIdx >= 0) {
                insertAfterIndex = statusIdx;
              } else {
                const anyIdx = projectOriginalAfterIndexToFiltered(findLastGoalPlanOriginalIndex({}));
                if (anyIdx >= 0) {
                  insertAfterIndex = anyIdx;
                }
              }
            }
          }
          const insertIndex = Math.min(Math.max(insertAfterIndex + 1, 0), filteredMessages.length);
          if (!insertions.has(insertIndex)) {
            insertions.set(insertIndex, []);
          }
          const insertionBucket = insertions.get(insertIndex);
          insertionBucket.push({
            role: 'assistant',
            content: '',
            badgeType: 'plan_block',
            badgeData: group,
          });
        });
        const output = [];
        let pendingAutoStepId = null;
        if (insertions.has(0)) {
          output.push(...insertions.get(0));
        }
        filteredMessages.forEach((entry, idx) => {
          const autoStepId = entry?.metadata?.autoStep?.stepId ?? null;
          if (autoStepId) {
            pendingAutoStepId = autoStepId;
          }
          const isPlainAssistant =
            entry?.role === 'assistant' &&
            !entry?.badgeType &&
            !entry?.preview &&
            !entry?.konvaPreview;
          if (pendingAutoStepId && isPlainAssistant) {
            output.push(entry);
            pendingAutoStepId = null;
          } else {
            output.push(entry);
          }
          const after = insertions.get(idx + 1);
          if (after?.length) {
            output.push(...after);
          }
        });
        return stripKonvaPreviews(output);
      }, [messages, subplanGroups, archivedGoalIdSet]);
      const PlanBlockCard = useCallback(
        ({ data }) => {
          if (!data) {
            return null;
          }
          const subplanId = data.id;
          const label = data.label ?? 'Plan steps';
          const steps = Array.isArray(data.steps) ? data.steps : [];
          const orderedSteps = buildSubplanTreeSteps(steps);
          const tree = [];
          const stack = [];
          orderedSteps.forEach(({ step, depth }) => {
            const node = { step, depth, children: [] };
            while (stack.length > depth) {
              stack.pop();
            }
            if (stack.length === 0) {
              tree.push(node);
            } else {
              stack[stack.length - 1].children.push(node);
            }
            stack[depth] = node;
          });
          const stateById = new Map();
          const resolvedStepStates = [];
          steps.forEach((step, idx) => {
            const stepId = resolvePlanStepId(step);
            const stepPrimitiveId = step?.primitiveId ?? null;
            const stepKey = stepId ?? stepPrimitiveId ?? idx;
            const state = derivePlanStepStatus(step, mainstore, activePlanStepId);
            stateById.set(stepKey, state);
            if (stepId) {
              stateById.set(stepId, state);
            }
            if (stepPrimitiveId) {
              stateById.set(stepPrimitiveId, state);
            }
            resolvedStepStates.push(state);
          });
          const isComplete = resolvedStepStates.length > 0
            ? resolvedStepStates.every((state) => state?.state === 'done' || state?.state === 'skipped')
            : Boolean(data.isComplete);
          const hasRunning = resolvedStepStates.some((state) => state?.state === 'running');
          const hasAwaiting = resolvedStepStates.some((state) => state?.label === 'Awaiting');
          const hasBlocked = resolvedStepStates.some((state) => state?.state === 'blocked');
          const hasQueued = resolvedStepStates.some((state) => state?.state === 'queued');
          const collapsedOverride = collapsedSubplanState?.[subplanId];
          const isCollapsed = collapsedOverride !== undefined ? collapsedOverride : isComplete;
          const blockStepIds = new Set();
          steps.forEach((step) => {
            const stepId = normalizeStepKey(resolvePlanStepId(step));
            const primitiveId = normalizeStepKey(step?.primitiveId ?? null);
            if (stepId) {
              blockStepIds.add(stepId);
            }
            if (primitiveId) {
              blockStepIds.add(primitiveId);
            }
          });
          const isBlockFocused =
            Boolean(focusedPlanStepId) && blockStepIds.has(String(focusedPlanStepId));
          const focusPlanBlock = (event) => {
            event?.stopPropagation?.();
            if (!onPlanStepFocus) {
              return;
            }
            if (isBlockFocused) {
              onPlanStepFocus(null);
              return;
            }
            const ordered = [...steps].sort((a, b) => {
              const aOrder = Number.isFinite(a?.order) ? a.order : Number.MAX_SAFE_INTEGER;
              const bOrder = Number.isFinite(b?.order) ? b.order : Number.MAX_SAFE_INTEGER;
              return aOrder - bOrder;
            });
            const first = ordered[0];
            const targetId = first?.id ?? first?.stepId ?? first?.primitiveId ?? null;
            const stepIds = ordered
              .map((step) => step?.id ?? step?.stepId ?? step?.primitiveId ?? null)
              .filter(Boolean);
            onPlanStepFocus({ anchorStepId: targetId ?? null, stepIds });
          };
          const summaryLabel = isComplete ? `${label} complete` : label;
          const headerStatus = isComplete ? (
            <span
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-100"
              title="Complete"
            >
              <HeroIcon icon="CheckCircleIcon" className="h-4 w-4" />
              <span className="sr-only">Complete</span>
            </span>
          ) : hasBlocked ? (
            <span
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-100"
              title="Blocked"
            >
              <HeroIcon icon="ExclamationTriangleIcon" className="h-4 w-4" />
              <span className="sr-only">Blocked</span>
            </span>
          ) : hasRunning ? (
            <span
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-100"
              title="Running"
            >
              <HeroIcon icon="ArrowPathIcon" className="h-4 w-4 animate-spin" />
              <span className="sr-only">Running</span>
            </span>
          ) : hasAwaiting ? (
            <span
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-100"
              title="Awaiting input"
            >
              <HeroIcon icon="PauseIcon" className="h-4 w-4" />
              <span className="sr-only">Awaiting input</span>
            </span>
          ) : hasQueued ? (
            <span
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-200"
              title="Queued"
            >
              <HeroIcon icon="ClockIcon" className="h-4 w-4" />
              <span className="sr-only">Queued</span>
            </span>
          ) : null;
          return (
            <div className="w-full">
              <div className="flex w-full flex-col gap-2 rounded-2xl border border-slate-200 bg-white text-left text-sm text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
                <div className="flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-left">
                  <button
                    type="button"
                    onClick={() => toggleSubplanCollapsed(subplanId)}
                    className="flex min-w-0 flex-1 items-center gap-2"
                  >
                    <div className="flex items-center gap-2">
                      {headerStatus ?? (
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                          <HeroIcon icon="PauseIcon" className="h-4 w-4" />
                        </span>
                      )}
                      <span className="truncate font-semibold text-slate-600 dark:text-slate-300">{summaryLabel}</span>
                      {hasAwaiting && !hasRunning && (
                        <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:border-amber-500/50 dark:bg-amber-900/40 dark:text-amber-100">
                          Awaiting input
                        </span>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    title={isBlockFocused ? "Clear artefact focus" : "Focus artefacts to this plan"}
                    onClick={focusPlanBlock}
                    className={clsx(
                      "inline-flex h-7 w-7 items-center justify-center rounded-full border transition",
                      isBlockFocused
                        ? "border-blue-200 bg-blue-50 text-blue-600 dark:border-blue-500/60 dark:bg-blue-900/30 dark:text-blue-200"
                        : "border-slate-200 bg-white text-slate-400 hover:border-slate-300 hover:text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400",
                    )}
                    disabled={!onPlanStepFocus || !steps.length}
                  >
                    <HeroIcon icon="EyeIcon" className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleSubplanCollapsed(subplanId)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-transparent text-slate-400 transition hover:border-slate-200 hover:text-slate-500 dark:hover:border-slate-700"
                    aria-label={isCollapsed ? "Expand plan" : "Collapse plan"}
                  >
                    <HeroIcon
                      icon="ChevronDownIcon"
                      className={clsx('h-4 w-4 transition', isCollapsed ? '' : 'rotate-180')}
                    />
                  </button>
                </div>
              {!isCollapsed && (
                <div className="flex flex-col gap-1 px-3 pb-2 text-[12px] text-slate-600 dark:text-slate-300">
                  {tree.map((node, idx) => (
                    <SubplanTreeNode
                      key={node.step?.id ?? node.step?.primitiveId ?? idx}
                      node={node}
                      depth={0}
                      isLastSibling={idx === tree.length - 1}
                      showSiblingConnector={idx < tree.length - 1}
                      stateById={stateById}
                      mainstore={mainstore}
                      activeStepId={activePlanStepId}
                      actionFeedback={planStepActionFeedback}
                      handlePlanStepActivate={handlePlanStepActivate}
                      handlePlanStepDelete={handlePlanStepDelete}
                    />
                  ))}
                </div>
              )}
              </div>
            </div>
          );
        },
        [
          collapsedSubplanState,
          mainstore,
          activePlanStepId,
          planStepActionFeedback,
          toggleSubplanCollapsed,
          focusedPlanStepId,
          normalizeStepKey,
          onPlanStepFocus,
          handlePlanStepActivate,
          handlePlanStepDelete,
        ]
      );
      const planStepPrimitiveIds = useMemo(
        () => planSteps.map((step) => step?.primitiveId).filter(Boolean),
        [planSteps]
      );

      const handleDeleteGoal = useCallback(async () => {
        if (!planPrimitiveId || !goalToDelete?.id || deletingGoal) {
          return;
        }
        setDeletingGoal(true);
        try {
          const response = await fetch(
            `/api/plan/${planPrimitiveId}/goals/${encodeURIComponent(goalToDelete.id)}`,
            { method: 'DELETE' }
          );
          const payload = await response.json().catch(() => null);
          if (!response.ok || !payload?.success) {
            throw new Error(payload?.error || 'Failed to delete goal');
          }
          if (typeof mainstore.fetchPrimitive === 'function') {
            await mainstore.fetchPrimitive(planPrimitiveId, { force: true });
          }
          bumpPlanArtifactVersion();
        } catch (error) {
          console.warn('Failed to delete goal', error);
        } finally {
          setDeletingGoal(false);
          setShowGoalDeleteConfirm(false);
          setGoalToDelete(null);
        }
      }, [planPrimitiveId, goalToDelete, deletingGoal, mainstore, bumpPlanArtifactVersion]);

      useEffect(() => {
        if (!planPrimitiveId || typeof mainstore.fetchPrimitive !== 'function') {
          return;
        }
        let cancelled = false;
        (async () => {
          try {
            await mainstore.fetchPrimitive(planPrimitiveId, { force: true });
            if (!cancelled) {
              bumpPlanArtifactVersion();
            }
          } catch (error) {
            console.warn('Failed to refresh plan primitive', error);
          }
        })();
        return () => {
          cancelled = true;
        };
      }, [planPrimitiveId, mainstore]);

      useEffect(() => {
        if (!planPrimitiveId) {
          setPlanArtifacts(null);
          return;
        }
        let cancelled = false;
        const planPrimitive = mainstore.primitive(planPrimitiveId);
        if (!planPrimitive) {
          if (!cancelled) {
            setPlanArtifacts(null);
          }
          return;
        }
        const snapshot = extractPlanFromPrimitive(planPrimitive);
        if (!cancelled) {
          setPlanArtifacts(snapshot);
        }
        return () => {
          cancelled = true;
        };
      }, [planPrimitiveId, planArtifactVersion, mainstore]);

      useDataEvent(planPrimitiveId ? PLAN_EVENT_FIELDS : null, planPrimitiveId ?? undefined, () =>
        bumpPlanArtifactVersion()
      );
      useDataEvent(
        planStepPrimitiveIds.length ? PLAN_EVENT_FIELDS : null,
        planStepPrimitiveIds.length ? planStepPrimitiveIds : undefined,
        () => bumpPlanArtifactVersion()
      );
      useEffect(() => {
        const nextPlanPrimitiveId = planPrimitiveId ?? null;
        if (!onPlanPrimitiveChange) {
          lastPlanPrimitiveIdRef.current = nextPlanPrimitiveId;
          lastPlanPrimitiveCallbackRef.current = null;
          return;
        }
        const callbackChanged = lastPlanPrimitiveCallbackRef.current !== onPlanPrimitiveChange;
        if (callbackChanged || lastPlanPrimitiveIdRef.current !== nextPlanPrimitiveId) {
          lastPlanPrimitiveIdRef.current = nextPlanPrimitiveId;
          lastPlanPrimitiveCallbackRef.current = onPlanPrimitiveChange;
          onPlanPrimitiveChange(nextPlanPrimitiveId);
        }
      }, [planPrimitiveId, onPlanPrimitiveChange]);
      const showContextPanel = props.showContext !== false && Array.isArray(context) && context.length > 0;

      useEffect(() => {
        if (!onTaskAreaChange) {
          return;
        }

        const nextArea = taskState?.area || "discovery";
        if (lastTaskAreaRef.current !== nextArea) {
          lastTaskAreaRef.current = nextArea;
          onTaskAreaChange(nextArea);
        }
      }, [taskState?.area, onTaskAreaChange]);

      useEffect(() => {
        if (!primitive?.id) {
          setAvailableChats([]);
          return;
        }

        const parent = mainstore.primitive(primitive.id) ?? primitive;
        const chatCollection = parent?.primitives?.chat.allItems ?? []

        const resolved = chatCollection.map((chat) => {
          const chatId = chat?.id ?? chat?._id;
          return mainstore.primitive(chatId) ?? chat;
        }).filter(Boolean);

        const seen = new Set();
        const deduped = [];
        for (const chat of resolved) {
          const cid = chat?.id ?? chat?._id;
          if (!cid || seen.has(cid)) {
            continue;
          }
          seen.add(cid);
          deduped.push(chat);
        }

        deduped.sort((a, b) => resolveChatUpdatedAtMs(b) - resolveChatUpdatedAtMs(a));

        setAvailableChats(deduped);
      }, [primitive?.id, mainstore, chatVersion]);

      const applyAgentMode = useCallback((modeData) => {
        if (!modeData) {
          setModeSummary({ available: [], active: null });
          return;
        }

        setModeSummary((prev) => {
          const nextAvailable = modeData.available ?? [];
          const prevAvailable = prev?.available ?? [];
          const unchanged =
            prev?.active === modeData.active &&
            prevAvailable.length === nextAvailable.length &&
            prevAvailable.every((item, idx) => {
              const candidate = nextAvailable[idx] ?? {};
              return (
                item.id === candidate.id &&
                item.label === candidate.label &&
                item.icon === candidate.icon
              );
            });

          if (unchanged) {
            return prev;
          }

          return {
            available: nextAvailable,
            active: modeData.active ?? null,
          };
        });
      }, []);

      const statusPinnedRef = useRef(false);
      const dismissedGoalPlanIdsRef = useRef(new Set());
      const persistChatHistoryRef = useRef(null);

      function setAgentStatus(status){
        if (!status) {
          setAgentStatusBadge(null);
          statusPinnedRef.current = false;
          return;
        }
        statusPinnedRef.current = status.startsWith('update:');
        setAgentStatusBadge(status);
      }
    
      useEffect(()=>{
        updateStatus( {active: !inputBox.current?.empty(), messages})
        if( externalContext ){
          const ext = [externalContext].flat().filter(Boolean)
          if( ext.map(d=>d?.id).join("-") !== [context].flat().filter(d=>Boolean).map(d=>d?.id).join("-")){
            setContext(ext)
          }
        }else{
          const contextMessages = messages.filter(d=>d.role === "assistant" && d.hidden).map(d=>d.content?.match(/\[\[chat_scope:([^\]]*)\]\]/)?.[1]?.split(",")).filter(d=>d)
          const latestContext = contextMessages.at(-1)?.map(d=>{
            const asNum = parseInt(d)
            if( isObjectId(d) || isNaN(asNum)){
              return mainstore.primitive(d)
            }else{
              return mainstore.primitive(asNum)
            }
          }).filter(d=>d)
          setContext( latestContext )
        }
          
        const stateMessages = messages.filter(d=>d.role === "assistant" && d.hidden).filter(d=>d.content?.startsWith("[[current_state:"))
        const latestStateMsg = stateMessages.at(-1)
        let latestState
        if( latestStateMsg ){
          const partial = latestStateMsg.content.slice(16,-2)
          console.log(partial)
          try{
            latestState = JSON.parse(partial)
          }catch(e){

          }
        }
        if( !deepEqualIgnoreOrder(latestState, chatState) ){
          if(props.setChatState ){
            props.setChatState( latestState )
          }
          setChatState( latestState )
        }
      }, [messages, externalContext, mainstore])

      useEffect(() => {
        messagesRef.current = messages;
      }, [messages]);

      useEffect(() => {
        if (typeof onChatSessionChange === "function") {
          onChatSessionChange(activeChatInfo);
        }
      }, [activeChatInfo, onChatSessionChange]);

      useImperativeHandle(
        ref,
        () => ({
          setContext: (context) => {
            setExternalContext(context);
          },
          getActiveChatInfo: () => activeChatInfo,
        }),
        [activeChatInfo],
      );

      useEffect(() => {
        if (subplanGroups.length === 0) {
          planBlocksRenderedRef.current = '';
        } else {
          planBlocksRenderedRef.current = planBlocksKey;
        }
      }, [subplanGroups.length, planBlocksKey]);

      const shouldAutoScrollRef = useRef(true);
      const handleTimelineScroll = useCallback(() => {
        const el = timelineRef.current;
        if (!el) {
          return;
        }
        shouldAutoScrollRef.current = el.scrollHeight - (el.scrollTop + el.clientHeight) <= 8;
      }, []);

      useEffect(() => {
        const el = timelineRef.current;
        if (!el) {
          return;
        }
        if (shouldAutoScrollRef.current) {
          el.scrollTop = el.scrollHeight;
        }
      }, [renderMessages, agentStatusBadge])


      function updateAssistantUI(text, hidden = false, other = {}) {
        setMessages(h => {
          const lastMsg = h[h.length - 1] ?? {}
          const lastMsgIsBadge = Boolean(lastMsg.badgeType || lastMsg.metadata?.advisorSuggestionId);
          if (
            lastMsg.role === 'assistant' &&
            !lastMsg.preview &&
            !lastMsg.context &&
            !other.context &&
            !lastMsgIsBadge
          ){
                return [...h.slice(0, -1), {...other, updated: true, role:'assistant', content: text, hidden }];
          }
          return [...h, {...other, role:'assistant', content: text, hidden }];
        });
      }

      const updateGoalPlanCardStatus = useCallback((status, goalPlanId = null) => {
        setMessages((prev) => {
          let updated = false;
          const next = prev.map((entry) => {
            if (!entry || entry.badgeType !== 'goal_plan') {
              return entry;
            }
            const entryGoalPlanId = resolveGoalPlanIdFromEntry(entry);
            if (goalPlanId && entryGoalPlanId && entryGoalPlanId !== goalPlanId) {
              return entry;
            }
            const badgeData = entry.badgeData ?? {};
            if (badgeData.status === status) {
              return entry;
            }
            updated = true;
            return {
              ...entry,
              badgeData: {
                ...badgeData,
                status,
              },
            };
          });
          if (!updated) {
            return prev;
          }
          messagesRef.current = next;
          if (activeChatInfo?.id) {
            persistChatHistoryRef.current?.(next, { chatId: activeChatInfo.id });
          }
          return next;
        });
      }, [setMessages, activeChatInfo?.id]);

      const removeGoalPlanCards = useCallback((goalPlanId = null, nextStatus = null, hide = true) => {
        setMessages((prev) => {
          let changed = false;
          const next = prev.map((entry) => {
            if (!entry || entry.badgeType !== 'goal_plan') {
              return entry;
            }
            const entryGoalPlanId = resolveGoalPlanIdFromEntry(entry);
            if (goalPlanId && entryGoalPlanId !== goalPlanId) {
              return entry;
            }
            changed = true;
            const badgeData = entry.badgeData ?? {};
            const resolvedStatus = nextStatus ?? badgeData.status ?? entry?.metadata?.goalPlanStatus ?? null;
            return {
              ...entry,
              ...(hide ? { hidden: true } : {}),
              badgeData: {
                ...badgeData,
                ...(resolvedStatus ? { status: resolvedStatus } : {}),
                dismissed: true,
              },
              metadata: {
                ...(entry.metadata ?? {}),
                ...(resolvedStatus ? { goalPlanStatus: resolvedStatus } : {}),
              },
            };
          });
          if (!changed) {
            return prev;
          }
          messagesRef.current = next;
          if (activeChatInfo?.id) {
            persistChatHistoryRef.current?.(next, { chatId: activeChatInfo.id });
          }
          return next;
        });
      }, [setMessages, activeChatInfo?.id]);

      const upsertGoalPlanCard = useCallback((payload = {}) => {
        const goalPlan = payload.plan ?? null;
        const goalPlanRef = payload.planRef ?? null;
        const goalPlanId =
          payload.goalPlanId ??
          goalPlan?.id ??
          goalPlanRef?.goalPlanId ??
          null;
        if (!goalPlanId) {
          return;
        }
        const status = payload.status ?? goalPlanRef?.status ?? 'pending';
        const applyUpsert = (source = []) => {
          let found = false;
          const buildGoalPlanBadgeEntry = (existingEntry = null) => {
            const existingBadge = existingEntry?.badgeData ?? {};
            const mergedPlan = goalPlan ?? existingBadge.plan ?? null;
            return {
              ...(existingEntry ?? {}),
              role: existingEntry?.role ?? 'assistant',
              content: typeof existingEntry?.content === 'string' ? existingEntry.content : '',
              badgeType: 'goal_plan',
              badgeData: {
                ...existingBadge,
                status,
                ...(mergedPlan ? { plan: mergedPlan } : {}),
                ...(goalPlanRef ? { planRef: goalPlanRef } : {}),
              },
              metadata: {
                ...(existingEntry?.metadata ?? {}),
                goalPlanId,
                goalPlanStatus: status,
                goalPlanCreatedAt:
                  goalPlan?.createdAt ??
                  goalPlanRef?.createdAt ??
                  existingEntry?.metadata?.goalPlanCreatedAt ??
                  null,
              },
            };
          };
          const next = source.map((entry) => {
            if (!entry || entry.badgeType !== 'goal_plan') {
              return entry;
            }
            if (resolveGoalPlanIdFromEntry(entry) !== goalPlanId) {
              return entry;
            }
            found = true;
            return buildGoalPlanBadgeEntry(entry);
          });
          if (found) {
            messagesRef.current = next;
            return next;
          }
          const placeholderIndex = next.findIndex((entry) => {
            if (!entry || entry.badgeType === 'goal_plan') {
              return false;
            }
            return resolveGoalPlanIdFromEntry(entry) === goalPlanId;
          });
          if (placeholderIndex >= 0) {
            const replaced = [...next];
            replaced[placeholderIndex] = buildGoalPlanBadgeEntry(replaced[placeholderIndex]);
            messagesRef.current = replaced;
            return replaced;
          }
          const appended = [
            ...next,
            buildGoalPlanBadgeEntry(),
          ];
          return appended;
        };

        const baseMessages = Array.isArray(messagesRef.current) ? messagesRef.current : [];
        const nextMessages = applyUpsert(baseMessages);
        messagesRef.current = nextMessages;
        setMessages(nextMessages);
        if (activeChatInfo?.id) {
          persistChatHistoryRef.current?.(nextMessages, { chatId: activeChatInfo.id });
        }
      }, [setMessages, activeChatInfo?.id]);

      const activeChatPrimitive = useMemo(() => {
        if (!activeChatInfo?.id) {
          return null;
        }
        return mainstore.primitive(activeChatInfo.id) ?? null;
      }, [mainstore, activeChatInfo?.id, chatVersion, categoryVersion]);

      const pendingAutoStep = activeChatPrimitive?.referenceParameters?.pending_auto_step ?? null;

      const relatedCategories = useMemo(() => {
        if (!activeChatPrimitive) {
          return [];
        }

        const seen = new Map();
        const register = (entry) => {
          if (!entry || entry.type !== 'category') {
            return;
          }
          const cid = entry.id ?? entry._id;
          if (!cid || seen.has(cid)) {
            return;
          }
          seen.set(cid, entry);
        };

        const collect = (candidate) => {
          if (!candidate) {
            return;
          }
          if (Array.isArray(candidate)) {
            candidate.forEach(register);
            return;
          }
          if (Array.isArray(candidate.allItems)) {
            candidate.allItems.forEach(register);
          }
          if (Array.isArray(candidate.allCategory)) {
            candidate.allCategory.forEach(register);
          }
          if (Array.isArray(candidate.items)) {
            candidate.items.forEach(register);
          }
        };

        const { primitives } = activeChatPrimitive;
        if (primitives) {
          collect(primitives.allCategory);
          collect(primitives.category?.allItems);
          collect(primitives.origin?.allCategory);
          collect(primitives.origin?.category?.allItems);
          collect(primitives.link?.allCategory);
          collect(primitives.link?.category?.allItems);
        }

        return Array.from(seen.values());
      }, [activeChatPrimitive, categoryVersion]);

      const trackedCategoryIds = useMemo(
        () => relatedCategories.map((category) => category?.id ?? category?._id).filter(Boolean),
        [relatedCategories]
      );

      useDataEvent('processing_update set_field set_status set_processing', trackedCategoryIds, () => bumpCategoryVersion());

      const describeProgress = useCallback((category) => {
        const progress = category?.progress;
        if (progress === null || progress === undefined) {
          return 'Processing...';
        }
        if (typeof progress === 'string') {
          return progress;
        }
        if (typeof progress === 'number') {
          const value = progress > 1 ? progress : progress * 100;
          return `${Math.round(value)}% complete`;
        }
        if (typeof progress === 'object') {
          if (typeof progress.message === 'string' && progress.message.trim().length > 0) {
            return progress.message;
          }
          if (typeof progress.percentage === 'number' && Number.isFinite(progress.percentage)) {
            const value = progress.percentage > 1 ? progress.percentage : progress.percentage * 100;
            return `${Math.round(value)}% complete`;
          }
        }
        return 'Processing...';
      }, []);

      const runningCategories = useMemo(
        () => relatedCategories.filter((category) => category?.isRunning),
        [relatedCategories]
      );

      const runningSummaries = useMemo(() => {
        if (!runningCategories.length) {
          return [];
        }
        return runningCategories.map((category) => ({
          id: category.id ?? category._id,
          title:
            category.title ||
            (category.plainId ? `Categorization #${category.plainId}` : 'Active categorization'),
          progress: describeProgress(category),
        }));
      }, [describeProgress, runningCategories]);

      const categorizationLocked = runningSummaries.length > 0;

      const removeAutoBlockedMessage = useCallback((stepId) => {
        if (!stepId) {
          return;
        }
        setMessages((prev) => {
          const index = prev.findIndex(
            (entry) => entry?.context?.autoBlockedStepId === stepId
          );
          if (index === -1) {
            return prev;
          }
          const next = [...prev.slice(0, index), ...prev.slice(index + 1)];
          messagesRef.current = next;
          return next;
        });
      }, []);

      useEffect(() => {
        if (!pendingAutoStep?.token) {
          if (!pendingAutoStep) {
            lastAutoStepTokenRef.current = null;
          }
          return;
        }
        if (pending || chatLoading || categorizationLocked) {
          return;
        }
        if (lastAutoStepTokenRef.current === pendingAutoStep.token) {
          return;
        }
        if (!sendChatRef.current) {
          return;
        }
        const autoMessage = buildAutoStepMessage(pendingAutoStep);
        if (!autoMessage) {
          lastAutoStepTokenRef.current = pendingAutoStep.token;
          return;
        }
        lastAutoStepTokenRef.current = pendingAutoStep.token;
        (async () => {
          try {
            const result = await sendChatRef.current?.({
              autoTrigger: true,
              autoMessage,
              options: {
                autoStepToken: pendingAutoStep.token,
              },
            });
            if (result === false) {
              lastAutoStepTokenRef.current = null;
            }
          } catch (error) {
            console.warn('Auto step handoff failed', error);
            lastAutoStepTokenRef.current = null;
          }
        })();
      }, [pendingAutoStep, pending, chatLoading, categorizationLocked]);

      async function sendChat(overrides = null) {
        if (pending || chatLoading || categorizationLocked) {
          return false;
        }
        const isAutoTrigger = overrides?.autoTrigger === true;
        let userMsg;
        const currentMessages = messagesRef.current ?? messages;

        if (isAutoTrigger) {
          userMsg = overrides?.autoMessage;
          if (!userMsg) {
            return false;
          }
        } else {
          const rawInput = inputBox.current?.value?.();
          const draft = typeof rawInput === 'string' ? rawInput.trim() : '';
          if (!draft) {
            return false;
          }
          userMsg = { role: 'user', content: draft };
        }
        const displayUserMsg = userMsg;
        let outboundUserMsg = userMsg;
        if (typeof outboundUserMsg?.content === 'string') {
          outboundUserMsg = translateAdvisorCommandByIndex(outboundUserMsg);
        }

        const activeSession = await ensureChatSession();
        if (!activeSession) {
          return false;
        }
        const sessionChatId = activeSession?.id ?? activeSession?._id;
        if (!sessionChatId) {
          return false;
        }

        const nextFull = [...currentMessages, displayUserMsg];
        setPending(true);
        setAgentStatus('agent_responding');
        setMessages(nextFull);
        messagesRef.current = nextFull;
        if (!isAutoTrigger) {
          inputBox.current?.clear();
        }

        updateStatus({ active: false, messages: nextFull });

        const immediateContextOverride = overrides?.immediateContext;
        const resolvedImmediateContext = immediateContextOverride
          ? immediateContextOverride
          : externalContext
            ? [externalContext].flat().map((d) => d.id)
            : undefined;

        const requestOptions = {
          parentId: primitive.origin?.id,
          agentScope: agentScope,
          immediateContext: resolvedImmediateContext,
          mode: chatMode ?? props.mode,
          chatPrimitiveId: activeSession.id,
          chatSessionKey: activeSession.sessionKey,
          ...(overrides?.options ?? {}),
        };

        const normalizedUserContent = String(outboundUserMsg?.content ?? '').trim().toLowerCase();
        if (normalizedUserContent.startsWith('goal accept')) {
          if (pendingGoalPlanForCard?.id) {
            dismissedGoalPlanIdsRef.current.add(pendingGoalPlanForCard.id);
            removeGoalPlanCards(pendingGoalPlanForCard.id, 'accepted', true);
          } else {
            removeGoalPlanCards(null, 'accepted', true);
          }
        } else if (normalizedUserContent.startsWith('goal decline')) {
          const declineMatch = normalizedUserContent.match(/^goal\s+decline(?:\s+(.+))?$/);
          const declineTarget = (declineMatch?.[1] ?? 'all').trim();
          if (!declineTarget || declineTarget === 'all') {
            const pendingId = pendingGoalPlanForCard?.id ?? taskState?.planReference?.pendingGoalPlan?.id ?? null;
            if (pendingId) {
              dismissedGoalPlanIdsRef.current.add(pendingId);
            }
            removeGoalPlanCards(pendingId, 'declined', true);
          } else {
            updateGoalPlanCardStatus('updated');
          }
        }
        const outboundMessages = filterAdvisorBadgeMessages([...currentMessages, outboundUserMsg]);

        const res = await fetch(`/api/primitive/${primitive.id}/agent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: outboundMessages,
            options: requestOptions,
          }),
        });
        if (!res.ok || !res.body) {
          setAgentStatus();
          setPending(false);
          return false;
        }
        const reader = res.body.getReader();
        const dec    = new TextDecoder();
        let buffer   = '';
      
        let displayContent = '';
      
        // cancel any prior in-flight stream
        if (readerRef.current) readerRef.current.cancel();
        readerRef.current = reader;
      
        // 4) Read & process chunks
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += dec.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop();
      
          for (const part of parts) {
            if (!part.startsWith('data:')) continue;
            const payload = JSON.parse(part.slice(5).trim());

            if (payload.agent_mode) {
              applyAgentMode(payload.agent_mode);
              continue;
            }

            if (payload.task_state !== undefined) {
              setTaskState(payload.task_state);
              continue;
            }

            const advisorSuggestionPayload = payload.advisorSuggestion;
            if (advisorSuggestionPayload) {
              setAdvisorSuggestions((prev) => {
                const filtered = prev.filter((entry) => entry.id !== advisorSuggestionPayload.id);
                return [advisorSuggestionPayload, ...filtered];
              });
              advisorSuggestionPlanIdRef.current = planPrimitiveId ?? advisorSuggestionPlanIdRef.current ?? null;
              upsertAdvisorBadgeMessage(advisorSuggestionPayload);
            }

            const skipAdvisorText = Boolean(advisorSuggestionPayload);

            if (payload.context?.goalPlan || payload.context?.goalPlanRef) {
              const goalPlan = payload.context?.goalPlan ?? null;
              const goalPlanRef = payload.context?.goalPlanRef ?? null;
              upsertGoalPlanCard({
                plan: goalPlan,
                planRef: goalPlanRef,
                goalPlanId: goalPlan?.id ?? goalPlanRef?.goalPlanId ?? null,
                status: goalPlanRef?.status ?? 'pending',
              });
              displayContent = '';
              continue;
            }

            if (payload.content && !skipAdvisorText) {
              let content = payload.content;
              const unblockMatches = [];
              content = content.replace(/\[\[auto_unblocked:([^\]]+)\]\]/g, (_, id) => {
                unblockMatches.push(id);
                return '';
              });
              let blockedStepId = null;
              content = content.replace(/\[\[auto_blocked:([^\]]+)\]\]/g, (_, id) => {
                blockedStepId = blockedStepId || id;
                return '';
              });
              unblockMatches.forEach((id) => removeAutoBlockedMessage(id));
              payload.content = content;

              if( payload.content.startsWith("__SC_BK")){
                const rewind = payload.content.match(/__SC_BK(\d+)__/)
                if( rewind ){
                  const rewindCount = parseInt(rewind[1])
                  displayContent = displayContent.slice(0, displayContent.length - rewindCount)
                  payload.content = payload.content.slice(rewind[0].length)
                }
              }
              let statusUpdate

              displayContent += payload.content;
              
              if( displayContent.match(/\[\[agent_running\]\]/)){
                statusUpdate = "[[agent_running]]"
                displayContent = displayContent.replace(statusUpdate, "" )
              }else  {
                const m = displayContent.match(/\[\[update:([^\]]*\]\])$/, "")
                if( m ){
                  statusUpdate = m[0]
                  displayContent = displayContent.replace(statusUpdate, "");
                }
              }
              if( statusUpdate ){
                setAgentStatus( statusUpdate.slice(2, -2) )
              }else{
                setAgentStatus( "agent_responding" )
              }
              const hasDisplayableContent =
                typeof displayContent === 'string' && displayContent.trim().length > 0;
              if (!payload.hidden && hasDisplayableContent) {
                const assistantExtras = blockedStepId
                  ? { context: { autoBlockedStepId: blockedStepId } }
                  : {};
                updateAssistantUI(displayContent, false, assistantExtras);
              }
              if (payload.hidden) {
                displayContent = ""
              }
            }else if(payload.context){
              let contextText = ""
              if( payload.context.context?.canCreate ){
                const action = "create"
                const text =
                  payload.context.context?.action_title ??
                  (payload.context.context?.type === "one_shot_query" ? "Go deeper" : "Add query to canvas")
                const itemId = actionData.current.length 
                actionData.current.push({
                  id: itemId,
                  action,
                  data: payload.context.context
                })
                contextText = `[[action_item:${itemId}:${text}]]`
              }
              const hidden = payload.context.context?.canCreate ? false :true
              console.log(hidden)
              updateAssistantUI(contextText, hidden, payload);
                displayContent = ""
            }else if(payload.preview){
              const {preview, ...other} =  payload
              updateAssistantUI(preview, false, {...other, preview: true});
                displayContent = ""
            }
      
            if (payload.done) {
              setAgentStatus()
              setPending(false)
              reader.cancel();
              readerRef.current = null;
              persistChatHistory(messagesRef.current, { chatId: sessionChatId });
              return true;
            }
          }
        }
      }
      sendChatRef.current = sendChat;

      const handleAdvisorAction = useCallback((action, suggestion, displayIndex) => {
        if (!suggestion?.id) {
          return;
        }
        let removedId = null;
        setAdvisorSuggestions((prev) => {
          if (!prev.length) {
            return prev;
          }
          const next = prev.filter((entry) => {
            const keep = entry.id !== suggestion.id;
            if (!keep && entry?.id) {
              removedId = entry.id;
            }
            return keep;
          });
          return next.length === prev.length ? prev : next;
        });
        if (removedId) {
          markAdvisorSuggestionsHandled([removedId]);
        }
        const lookupNumber = typeof displayIndex === 'number' ? displayIndex : advisorSuggestionNumberLookup.idToNumber.get(suggestion.id);
        const commandValue = lookupNumber || suggestion.id;
        const command = `advisor ${action} ${commandValue}`;
        if (sendChatRef.current) {
          sendChatRef.current({
            autoTrigger: true,
            autoMessage: { role: 'user', content: command },
          });
        }
      }, [advisorSuggestionNumberLookup, markAdvisorSuggestionsHandled]);
      const handleGoalPlanAction = useCallback((action, target) => {
        const targetToken = typeof target === 'number' ? String(target) : target;
        const command =
          action === 'accept'
            ? `goal accept ${targetToken ?? 'all'}`
            : `goal decline ${targetToken ?? 'all'}`;
        if (action === 'accept') {
          const pendingId = pendingGoalPlanForCard?.id ?? taskState?.planReference?.pendingGoalPlan?.id ?? null;
          if (pendingId) {
            dismissedGoalPlanIdsRef.current.add(pendingId);
          }
          removeGoalPlanCards(pendingId, 'accepted', true);
        } else if (targetToken === 'all') {
          const pendingId = pendingGoalPlanForCard?.id ?? taskState?.planReference?.pendingGoalPlan?.id ?? null;
          if (pendingId) {
            dismissedGoalPlanIdsRef.current.add(pendingId);
          }
          removeGoalPlanCards(pendingId, 'declined', true);
        } else {
          updateGoalPlanCardStatus('updated');
        }
        if (sendChatRef.current) {
          sendChatRef.current({
            autoTrigger: true,
            autoMessage: { role: 'user', content: command },
          });
        }
      }, [updateGoalPlanCardStatus, removeGoalPlanCards, pendingGoalPlanForCard?.id, taskState?.planReference?.pendingGoalPlan?.id]);
      const advisorBadgeRenderers = useMemo(() => {
        return {
          advisor: ({ data }) => {
            if (!data || !data.id || !visibleAdvisorSuggestionIds.has(data.id)) {
              return null;
            }
            const suggestion = advisorSuggestionMap.get(data.id);
            const displayIndex = advisorSuggestionNumberLookup.idToNumber.get(data.id);
            return (
              <AdvisorSuggestionBadge
                data={data}
                suggestion={suggestion}
                onAction={handleAdvisorAction}
                displayIndex={displayIndex}
              />
            );
          },
          goal_plan: ({ data }) => {
            if (!data) {
              return null;
            }
            const planGoalIds = (Array.isArray(data?.plan?.goals) ? data.plan.goals : [])
              .map((goal) => goal?.id)
              .filter(Boolean);
            const isPlanFocused =
              Boolean(focusedGoalId) && planGoalIds.some((goalId) => String(goalId) === String(focusedGoalId));
            return (
              <GoalPlanCard
                data={data}
                onAction={handleGoalPlanAction}
                onFocusPlan={() => focusGoalPlanArtefacts(planGoalIds)}
                planFocused={isPlanFocused}
              />
            );
          },
          plan_block: ({ data }) => (
            <PlanBlockCard
              key={`${data?.id ?? 'plan'}:${activePlanStepId ?? 'none'}:${planBlocksKey}`}
              data={data}
            />
          ),
        };
      }, [
        advisorSuggestionMap,
        visibleAdvisorSuggestionIds,
        advisorSuggestionNumberLookup,
        handleAdvisorAction,
        handleGoalPlanAction,
        focusGoalPlanArtefacts,
        focusedGoalId,
        PlanBlockCard,
        activePlanStepId,
        planBlocksKey,
      ]);

      const immediateContextIds = useMemo(() => {
        const source = externalContext && [externalContext].flat().filter(Boolean).length > 0
          ? [externalContext].flat().filter(Boolean)
          : [context].flat().filter(Boolean);
        return source?.map((item) => item?.id)?.filter(Boolean) ?? [];
      }, [externalContext, context]);

      const refreshModeSummary = useCallback(async (ids) => {
        if (!primitive?.id) {
          return;
        }

        const controller = new AbortController();
        if (modeRefreshController.current) {
          modeRefreshController.current.abort();
        }
        modeRefreshController.current = controller;

        let reader;
        try {
          
          const res = await fetch(`/api/primitive/${primitive.id}/agent`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              messages: [],
              options: {
                parentId: primitive.origin?.id,
                agentScope,
                immediateContext: ids?.length ? ids : undefined,
                modePing: true,
              },
            }),
            signal: controller.signal,
          });

          if (!res.ok || !res.body) {
            return;
          }

          reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let finished = false;

          while (!finished) {
            const { value, done } = await reader.read();
            if (done) {
              break;
            }
            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop();
            for (const part of parts) {
              if (!part.startsWith('data:')) continue;
              const payload = JSON.parse(part.slice(5).trim());
              if (payload.agent_mode) {
                applyAgentMode(payload.agent_mode);
              }
              if (payload.task_state !== undefined) {
                setTaskState(payload.task_state);
              }
              if (payload.done) {
                finished = true;
              }
            }
          }
        } catch (error) {
          if (!controller.signal.aborted && error.name !== 'AbortError') {
            console.warn('Mode refresh failed', error);
          }
        } finally {
          if (reader) {
            try {
              await reader.cancel?.();
            } catch (_) {
              // ignore cancellation errors
            }
            reader.releaseLock?.();
          }

          if (modeRefreshController.current === controller) {
            modeRefreshController.current = null;
          }
        }
      }, [primitive?.id, primitive?.origin?.id, agentScope?.constrainTo, applyAgentMode]);

      const modeRefreshKey = immediateContextIds.length ? immediateContextIds.join(',') : 'none';

      useEffect(() => {
        refreshModeSummary(immediateContextIds);
      }, [refreshModeSummary, modeRefreshKey]);

      useEffect(() => () => {
        modeRefreshController.current?.abort?.();
      }, []);

      function handleInputKeyPress(e){
        if(e.key === "Enter"){
            if( !e.altKey && !categorizationLocked ){
                sendChat()
                return true
            }
        }
        return false
      }
      function updateStatus(status = {}){
        if(props.setStatus){
          props.setStatus({
            activeChat: status.active || status.messages?.length > 0,
            hasReplies: status.messages?.length > 0
          })
        }
      }
      const persistChatHistory = useCallback(
        async (history, options = {}) => {
          const targetChatId = options.chatId ?? options.chatPrimitiveId ?? activeChatInfo?.id;
          if (!targetChatId) {
            return;
          }

          let chat = mainstore.primitive(targetChatId);
          if (!chat && typeof mainstore.fetchPrimitive === 'function') {
            try {
              await mainstore.fetchPrimitive(targetChatId);
              chat = mainstore.primitive(targetChatId);
            } catch (error) {
              console.warn('Failed to load chat for persistence', error);
            }
          }

          if (!chat || typeof chat.setField !== 'function') {
            return;
          }

          const sanitizedHistory = sanitizeChatHistory(history);

          try {
            await chat.setField('referenceParameters.chat_history', sanitizedHistory);
            const chatId = chat.id ?? chat._id ?? targetChatId;
            setActiveChatInfo((prev) => {
              if (!prev || prev.id !== chatId) {
                return prev;
              }
              const refParams = chat.referenceParameters ?? {};
              const persistedPlanPrimitiveId =
                refParams?.agent_state?.session?.task?.planPrimitiveId ??
                refParams?.planPrimitiveId ??
                refParams?.plan_primitive_id ??
                prev.planPrimitiveId ??
                null;
              return {
                ...prev,
                sessionKey: chat.referenceParameters.session_key ?? prev.sessionKey,
                updatedAt: chat.referenceParameters.updated_at,
                planPrimitiveId: persistedPlanPrimitiveId,
              };
            });
            bumpChatVersion();
          } catch (error) {
            console.warn('Failed to persist chat history', error);
          }
        },
        [activeChatInfo?.id, mainstore, bumpChatVersion]
      );

      useEffect(() => {
        persistChatHistoryRef.current = persistChatHistory;
      }, [persistChatHistory]);

      useEffect(() => {
        const pendingGoalPlan = pendingGoalPlanForCard;
        if (!pendingGoalPlan?.id) {
          return;
        }
        setMessages((prev) => {
          let changed = false;
          let next = prev.filter((entry) => {
            if (!entry || entry.badgeType !== 'goal_plan') {
              return true;
            }
            if (resolveGoalPlanIdFromEntry(entry) === pendingGoalPlan.id) {
              return true;
            }
            const status = entry?.badgeData?.status ?? entry?.metadata?.goalPlanStatus ?? null;
            return status && status !== 'pending';
          });
          if (next.length !== prev.length) {
            changed = true;
          }
          let found = false;
          next = next.map((entry) => {
            if (!entry || entry.badgeType !== 'goal_plan') {
              return entry;
            }
            if (resolveGoalPlanIdFromEntry(entry) !== pendingGoalPlan.id) {
              return entry;
            }
            found = true;
            const existingBadge = entry.badgeData ?? {};
            const existingPlan = existingBadge.plan ?? null;
            const mergedPlan = pendingGoalPlan ?? existingPlan;
            if (
              existingBadge.status === 'pending' &&
              existingPlan === mergedPlan &&
              entry?.metadata?.goalPlanStatus === 'pending'
            ) {
              return entry;
            }
            changed = true;
            return {
              ...entry,
              badgeData: {
                ...existingBadge,
                status: 'pending',
                ...(mergedPlan ? { plan: mergedPlan } : {}),
              },
              metadata: {
                ...(entry.metadata ?? {}),
                goalPlanId: pendingGoalPlan.id,
                goalPlanStatus: 'pending',
                goalPlanCreatedAt:
                  pendingGoalPlan?.createdAt ??
                  entry?.metadata?.goalPlanCreatedAt ??
                  null,
              },
            };
          });
          if (!found) {
            changed = true;
            const placeholderIndex = next.findIndex((entry) => {
              if (!entry || entry.badgeType === 'goal_plan') {
                return false;
              }
              return resolveGoalPlanIdFromEntry(entry) === pendingGoalPlan.id;
            });
            const cardEntry = {
              role: 'assistant',
              content:
                placeholderIndex >= 0 && typeof next[placeholderIndex]?.content === 'string'
                  ? next[placeholderIndex].content
                  : '',
              badgeType: 'goal_plan',
              badgeData: {
                status: 'pending',
                plan: pendingGoalPlan,
              },
              metadata: {
                goalPlanId: pendingGoalPlan.id,
                goalPlanStatus: 'pending',
                goalPlanCreatedAt: pendingGoalPlan?.createdAt ?? null,
              },
            };
            if (placeholderIndex >= 0) {
              next = [...next];
              next[placeholderIndex] = cardEntry;
            } else {
              next = [...next, cardEntry];
            }
          }
          if (!changed) {
            return prev;
          }
          messagesRef.current = next;
          if (activeChatInfo?.id) {
            persistChatHistory(next, { chatId: activeChatInfo.id });
          }
          return next;
        });
      }, [pendingGoalPlanSyncKey, pendingGoalPlanForCard, setMessages, activeChatInfo?.id, persistChatHistory]);

      const applyChatSelection = useCallback(async (chatId, chatData) => {
        if (!chatId) {
          setActiveChatInfo(null);
          actionData.current = [];
          setMessages([]);
          messagesRef.current = [];
          inputBox.current?.clear?.();
          setAgentStatus();
          setPending(false);
          setTaskState(null);
          updateStatus({ active: false, messages: [] });
          return null;
        }

        setChatLoading(true);
        try {
          const resolvedId = chatId ?? chatData?.id ?? chatData?._id;
          let target = chatData ?? mainstore.primitive(resolvedId);
          if (!target && mainstore.fetchPrimitive) {
            await mainstore.fetchPrimitive(resolvedId);
            target = mainstore.primitive(resolvedId);
          }

          if (!target) {
            return null;
          }

	        const referenceParams = target.referenceParameters ?? {};
	        const history = Array.isArray(referenceParams.chat_history) ? referenceParams.chat_history : [];
	        const sessionState = referenceParams.agent_state?.session ?? null;
          const linkedPlanPrimitiveId =
            target?.primitives?.link?.allPlan?.[0]?.id ??
            target?.primitives?.origin?.allPlan?.[0]?.id ??
            target?.primitives?.link?.allStep?.find((step) => step?.referenceParameters?.planPrimitiveId)
              ?.referenceParameters?.planPrimitiveId ??
            target?.primitives?.origin?.allStep?.find((step) => step?.referenceParameters?.planPrimitiveId)
              ?.referenceParameters?.planPrimitiveId ??
            null;
	        const sessionPlanPrimitiveId =
            sessionState?.task?.planPrimitiveId ??
            referenceParams?.planPrimitiveId ??
            referenceParams?.plan_primitive_id ??
            linkedPlanPrimitiveId ??
            null;
	        const persistedAdvisorSuggestions = extractActiveAdvisorSuggestionsFromHistory(history);

          actionData.current = [];
        setMessages(history);
        messagesRef.current = history;
        setAdvisorSuggestions(persistedAdvisorSuggestions);
        advisorSuggestionPlanIdRef.current = sessionPlanPrimitiveId;
        inputBox.current?.clear?.();
        setAgentStatus();
        setPending(false);

          if (sessionState) {
            setChatState(sessionState);
            props.setChatState?.(sessionState);
            setTaskState(sessionState.task ?? null);
          } else {
            setChatState({});
            props.setChatState?.(undefined);
            setTaskState(null);
          }

	          const info = {
	            id: target.id ?? target._id ?? resolvedId,
	            sessionKey: target.referenceParameters?.session_key,
	            title: target.title,
	            updatedAt: target.referenceParameters?.updated_at,
              planPrimitiveId: sessionPlanPrimitiveId,
	          };
          setActiveChatInfo(info);
          updateStatus({ active: history.length > 0, messages: history });
          return info;
        } finally {
          setChatLoading(false);
        }
      }, [mainstore, props.setChatState, setAdvisorSuggestions, updateStatus]);

      useEffect(() => {
        if (!activeChatInfo?.id) {
          return;
        }

        const targetId = activeChatInfo.id;
        const updated = availableChats.find((chat) => {
          const cid = chat?.id ?? chat?._id;
          return cid === targetId;
        });

        if (!updated) {
          return;
        }

        const nextSessionKey = updated.referenceParameters?.session_key;
        const nextUpdatedAt = updated.referenceParameters?.updated_at;
        const nextTitle = updated.title ?? updated.referenceParameters?.title;

        setActiveChatInfo((prev) => {
          if (!prev || prev.id !== targetId) {
            return prev;
          }

          const sameSession = prev.sessionKey === nextSessionKey || (!nextSessionKey && !prev.sessionKey);
          const sameUpdatedAt = prev.updatedAt === nextUpdatedAt || (!nextUpdatedAt && !prev.updatedAt);
          const sameTitle = (!nextTitle && !prev.title) || prev.title === nextTitle;

          if (sameSession && sameUpdatedAt && sameTitle) {
            return prev;
          }

          return {
            ...prev,
            sessionKey: nextSessionKey ?? prev.sessionKey,
            updatedAt: nextUpdatedAt ?? prev.updatedAt,
            title: nextTitle ?? prev.title,
          };
        });
      }, [availableChats, activeChatInfo?.id]);

      const createChatSession = useCallback(async () => {
        if (!primitive?.id || typeof mainstore.createPrimitive !== 'function') {
          return null;
        }

        setChatLoading(true);
        try {
          let parentPrimitive = mainstore.primitive(primitive.id);
          if (!parentPrimitive && typeof mainstore.fetchPrimitive === 'function') {
            await mainstore.fetchPrimitive(primitive.id);
            parentPrimitive = mainstore.primitive(primitive.id);
          }

          if (!parentPrimitive) {
            parentPrimitive = primitive;
          }

          const now = new Date().toISOString();
          const title = primitive?.title ? `Agent chat - ${primitive.title}` : 'Agent chat';
          const created = await mainstore.createPrimitive({
            title,
            type: 'chat',
            parent: parentPrimitive,
            parentPath: 'chat',
            workspaceId: primitive.workspaceId,
            referenceParameters: {
              chat_history: [],
              updated_at: now,
            },
          });

          if (!created) {
            return null;
          }

          bumpChatVersion();
          return await applyChatSelection(created.id ?? created._id, created);
        } catch (error) {
          console.warn('Failed to create chat', error);
          return null;
        } finally {
          setChatLoading(false);
        }
      }, [primitive, mainstore, applyChatSelection, bumpChatVersion]);

      const ensureChatSession = useCallback(async () => {
        if (activeChatInfo?.id) {
          return activeChatInfo;
        }
        return await createChatSession();
      }, [activeChatInfo, createChatSession]);

      const handleSelectChat = useCallback(async (chatId) => {
        await applyChatSelection(chatId);
      }, [applyChatSelection]);

      const handleStartNewChat = useCallback(async () => {
        await createChatSession();
      }, [createChatSession]);

      const handleDeleteChat = useCallback(async () => {
        if (!activeChatInfo?.id) {
          return;
        }
        setChatLoading(true);
        try {
          const target = mainstore.primitive(activeChatInfo.id) ?? { id: activeChatInfo.id };
          await mainstore.removePrimitive(target);
          bumpChatVersion();
          setActiveChatInfo(null);
          actionData.current = [];
          setMessages([]);
          messagesRef.current = [];
          inputBox.current?.clear?.();
          setAgentStatus();
          setPending(false);
          updateStatus({ active: false, messages: [] });
        } catch (error) {
          console.warn('Failed to delete chat', error);
        } finally {
          setChatLoading(false);
        }
      }, [activeChatInfo?.id, mainstore, bumpChatVersion, updateStatus]);
      useEffect(() => {
        if (!availableChats.length) {
          if (activeChatInfo) {
            setActiveChatInfo(null);
            actionData.current = [];
            setMessages([]);
            messagesRef.current = [];
            inputBox.current?.clear?.();
            setAgentStatus();
            setPending(false);
            updateStatus({ active: false, messages: [] });
          }
          return;
        }

        const exists = activeChatInfo && availableChats.some((chat) => {
          const cid = chat?.id ?? chat?._id;
          return cid === activeChatInfo.id;
        });

        if (!exists) {
          const nextChat = availableChats[0];
          applyChatSelection(nextChat?.id ?? nextChat?._id);
        }
      }, [availableChats, activeChatInfo, applyChatSelection, updateStatus]);
      function rewind(){
        const msgToRemove = messages.at(-1)
        if (!msgToRemove) {
          return
        }
        if( msgToRemove.hidden ){
          console.log(`SKIP REMOVAL FROM SLATE OF HIDDEN MESSAFE`)
        }
        const trimmed = messages.slice(0,  msgToRemove.hidden ? -2 : -1)
        setMessages(trimmed)
        setPending(false)
        console.log(msgToRemove)
        persistChatHistory(trimmed)
      }
      function clear(){
        setMessages([])
        inputBox.current.clear()
        inputBox.current.focus()
        updateStatus({active: true, messages: []})
        actionData.current = []
        setPending(false)
        persistChatHistory([])
      }
      function actionCallback(id){
        const {action, data} = actionData.current[id]
        console.log(data)
        mainstore.doPrimitiveAction( primitive, `run_agent_${action}`, data)
      }
    
      function handleInputFocus(){
        updateStatus({active: true, messages})
      }
      function handleInputBlur(){
        updateStatus({active: !inputBox.current?.empty(), messages})
      }
      const rootClass = clsx(
        'flex h-full flex-col overflow-hidden',
        compactMode ? 'text-[13px] leading-tight' : 'text-[15px] leading-normal'
      );

      return (
        <>
        <div className={rootClass}>
          <ChatSessionToolbar
            availableChats={availableChats}
            activeChatId={activeChatInfo?.id ?? null}
            activeUpdatedAt={activeChatInfo?.updatedAt ?? null}
            pending={pending}
            chatLoading={chatLoading}
            onSelect={handleSelectChat}
            onCreate={handleStartNewChat}
            onDelete={handleDeleteChat}
            hideSessionControls={hideChatSession}
            hideUpdatedTimestamp={hideChatUpdatedTimestamp}
          />
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {showContextPanel ? (
              <div
                className="mb-2 w-full flex-shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 shadow-sm dark:border-slate-700 dark:bg-slate-900"
                style={{ minHeight: '200px', maxHeight: '20vh' }}
              >
                <div className="flex items-center justify-between pb-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-300">Context</p>
                  <span className="text-[11px] text-slate-500 dark:text-slate-300">{context.length} source(s)</span>
                </div>
                <div className="flex h-full flex-col gap-2 overflow-y-auto pr-1">
                  {context.map((d) => (
                    <div
                      key={d.id ?? d._id}
                      onClick={props.contextClick ? () => props.contextClick(d) : undefined}
                      className="flex items-center space-x-2 rounded-md border border-slate-200 px-2 py-1 hover:border-slate-300 hover:shadow-sm dark:border-slate-700 dark:hover:border-slate-500"
                    >
                      {d.metadata?.icon && (
                        <HeroIcon icon={d.metadata.icon} className="h-4 w-4 text-slate-500 dark:text-slate-300" strokeWidth={1} />
                      )}
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-xs font-medium text-slate-800 dark:text-slate-100">{d.title}</span>
                        <span className="truncate text-[11px] text-slate-500 dark:text-slate-300">
                          {d.displayType} #{d.plainId}
                        </span>
                      </div>
                      <Badge color="zinc" className="ml-auto text-[10px]">
                        {d.itemsForProcessing?.length ?? 0} items
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="flex-1 min-h-0">
              <div className="mb-2 flex h-full w-full overflow-hidden rounded-lg border border-slate-100 bg-white/60 dark:border-slate-700 dark:bg-slate-900/70">
                <div
                  ref={timelineRef}
                  onScroll={handleTimelineScroll}
                  className="flex h-full w-full flex-col overflow-y-auto"
                >
                  {renderMessages.length > 0 ? (
                    <ChatTimeline
                      messages={renderMessages}
                      badgeRenderers={advisorBadgeRenderers}
                      actionCallback={actionCallback}
                      compactMode={compactMode}
                      statusBadge={agentStatusBadge}
                    />
                  ) : (
                    <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-transparent px-4 text-sm text-slate-400 dark:text-slate-500">
                      Start the chat to see the conversation here.
                    </div>
                  )}
                </div>
              </div>
            </div>
            {modeSummary?.available?.length > 0 && (
              <div className="mb-2 flex w-full flex-wrap items-center gap-2">
                {modeSummary.available.map((mode) => {
                  const icon = mode.icon || MODE_ICON_MAP[mode.id] || DEFAULT_MODE_ICON;
                  const isActive = mode.id === modeSummary.active;
                  return (
                    <div
                      key={mode.id}
                      className={clsx(
                        'flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium border transition-colors',
                        isActive
                          ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
                          : 'border-transparent bg-slate-100 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200'
                      )}
                    >
                      <HeroIcon icon={icon} className={clsx('h-4 w-4', isActive ? 'text-white' : 'text-slate-500 dark:text-slate-300')} />
                      <span>{mode.label || mode.id}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {runningSummaries.length > 0 && (
              <div className="mt-2 flex w-full items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:border-amber-400/70 dark:bg-amber-500/10 dark:text-amber-200">
                <HeroIcon icon="Cog6ToothIcon" className="h-5 w-5 animate-spin text-amber-600" />
                <div className="flex flex-col gap-1">
                  <span className="font-medium">Categorization running - chat is temporarily paused.</span>
                  <ul className="list-disc space-y-0.5 pl-4">
                    {runningSummaries.map((item) => (
                      <li key={item.id}>
                        <span className="font-semibold">{item.title}</span>
                        {item.progress ? ` · ${item.progress}` : null}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {awaitingInputDetails.length > 0 && (
              <div className="mt-2 flex w-full max-w-full items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700 shadow-sm dark:border-amber-400/70 dark:bg-amber-500/10 dark:text-amber-200">
                <HeroIcon icon="PauseIcon" className="h-5 w-5 text-amber-600" />
                <div className="flex flex-col gap-1">
                  <span className="font-medium">Awaiting your input</span>
                  <ul className="list-disc space-y-0.5 pl-4">
                    {awaitingInputDetails.map((entry, idx) => (
                      <li key={`${entry.title}-${idx}`}>
                        <span className="font-semibold">{entry.title}</span>
                        {entry.lines.map((line, lineIdx) => (
                          <div key={lineIdx}>{line}</div>
                        ))}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            <div
              className={clsx([
                'w-full flex space-x-2',
                props.seperateInput ? 'mt-4 rounded-lg border bg-white p-3 shadow-lg dark:border-slate-700 dark:bg-slate-900' : 'pt-3',
              ])}
              style={{ maxHeight: '20vh' }}
            >
              <div className="flex flex-1 items-stretch overflow-y-auto">
                <MarkdownEditor
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  ref={inputBox}
                  initialMarkdown={''}
                  onKeyUp={handleInputKeyPress}
                  float={props.seperateInput}
                />
              </div>
              <Button
                variant="light"
                radius="full"
                isIconOnly
                size="sm"
                onPress={() => sendChat()}
                isDisabled={pending || chatLoading || categorizationLocked}
              >
                <Icon icon="solar:round-arrow-up-linear" className="h-6 w-6 text-default-600 hover:text-default-800" />
              </Button>
              {messages.length > 0 && (
                <Button
                  variant="light"
                  radius="full"
                  isIconOnly
                  size="sm"
                  onPress={rewind}
                  isDisabled={pending || chatLoading || categorizationLocked}
                >
                  <Icon icon="solar:rewind-back-circle-outline" className="h-6 w-6 text-default-600 hover:text-default-800" />
                </Button>
              )}
              {messages.length > 0 && (
                <Button
                  variant="light"
                  radius="full"
                  isIconOnly
                  size="sm"
                  onPress={clear}
                  isDisabled={pending || chatLoading || categorizationLocked}
                >
                  <Icon icon="solar:trash-bin-trash-linear" className="h-6 w-6 text-default-600 hover:text-default-800" />
                </Button>
              )}
            </div>
          </div>
        </div>
        {showGoalDeleteConfirm && (
          <ConfirmationPopup
            title="Delete goal?"
            message="This will remove the goal and its steps that are not used elsewhere. This can't be undone."
            confirmText="Delete goal"
            confirmColor="danger"
            confirm={handleDeleteGoal}
            cancel={() => setShowGoalDeleteConfirm(false)}
          />
        )}
        </>
      );
})
export default AgentChat
